import { useState, useRef, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import api from '../../hooks/useApi';
import Modal from '../shared/Modal';

// ── Column mappings ───────────────────────────────────────────────────────────
const norm = h => String(h).toLowerCase().trim().replace(/\s+/g, ' ');

const SOW_COL_MAP = {
  'reference':          'reference',
  'triage notes':       'triage_notes',
  'triage':             'triage_notes',
  'status':             'status',
  'date received':      'date_received',
  'source':             'source',
  'sourcing event id':  'sourcing_event_id',
  'event id':           'sourcing_event_id',
  'sourcing deadline':  'sourcing_deadline',
  'deadline':           'sourcing_deadline',
  'owner':              'owner',
  'type':               'type',
  'client':             'client',
  'location':           'location',
  'contact':            'contact',
  'detail':             'detail',
  'notes':              'notes',
  'issued version':     'issued_version',
  'version':            'issued_version',
  'sow date':           'sow_date',
  'total excl vat':     'total_excl_vat',
  'total exc vat':      'total_excl_vat',
  'total excl. vat':    'total_excl_vat',
};

const MILESTONE_COL_MAP = {
  'reference':              'sow_reference',
  'po arrived':             'po_arrived',
  'milestone':              'milestone_code',
  'status':                 'status',
  'contract date':          'contract_date',
  'forecast date':          'forecast_date',
  'description':            'description',
  'invoice amount excl':    'invoice_amount_excl',
  'invoice amount incl':    'invoice_amount_incl',
  'invoice number':         'invoice_number',
  'formal invoice date':    'formal_invoice_date',
  'invoice date':           'formal_invoice_date',
  'date invoiced':          'formal_invoice_date',
  'coupa approved':         'coupa_approved',
  'est pay date':           'est_pay_date',
  'est pay':                'est_pay_date',
  'paid date':              'paid_date',
  'customer po':            'customer_po',
  'payment terms':          'payment_terms',
  'detail':                 'detail',
  'delivery comments':      'delivery_comments',
  'project manager':        'project_manager',
  'assigned to':            'assigned_to',
  'contact':                'contact',
  'paid':                   'paid_date',
};

const CATALOG_COL_MAP = {
  'code':            'code',
  'description':     'description',
  'category':        'category',
  'manufacturer':    'manufacturer',
  'sell price':      'sell_price',
  'sell_price':      'sell_price',
  'sow price':       'sell_price',
  'sow price £':     'sell_price',
  'commission %':    'commission_pct',
  'commission_pct':  'commission_pct',
  'commission pct':  'commission_pct',
  'commission':      'commission_pct',
  'notes':           'notes',
  'active':          'is_active',
  'is_active':       'is_active',
  'is active':       'is_active',
};

const PO_COL_MAP = {
  'po number':             'po_number',
  'po#':                   'po_number',
  'po no':                 'po_number',
  'po no.':                'po_number',
  'purchase order':        'po_number',
  'purchase order number': 'po_number',
  'subject / description': 'description',
  'subject':               'description',
  'description':           'description',
  'supplier':              'supplier',
  'vendor':                'supplier',
  'value (£)':             'value',
  'value':                 'value',
  'amount':                'value',
  'net amount':            'value',
  'net value':             'value',
  'net':                   'value',
  'total':                 'value',
  'total excl vat':        'value',
  'total excl. vat':       'value',
  'ex vat':                'value',
  'excl vat':              'value',
  'cost':                  'value',
  'price':                 'value',
  'po value':              'value',
  'order value':           'value',
  'date issued':           'date_issued',
  'date':                  'date_issued',
  'order date':            'date_issued',
  'issue date':            'date_issued',
  'status':                'status',
  'av reference':          'sow_reference',
  'av ref':                'sow_reference',
  'sow reference':         'sow_reference',
  'sow ref':               'sow_reference',
  'project reference':     'sow_reference',
  'raised by':             'raised_by',
  'requester':             'raised_by',
  'requested by':          'raised_by',
  'notes':                 'notes',
};

// ── InvenTree inventory import ────────────────────────────────────────────────
const INVENTREE_STATUS_MAP = {
  'ok':          'in_stock',
  'attention':   'in_stock',
  'damaged':     'in_stock',
  'destroyed':   'decommissioned',
  'rejected':    'decommissioned',
  'lost':        'decommissioned',
  'quarantined': 'in_stock',
  'returned':    'in_stock',
};

const VALID_INVENTORY_STATUSES = ['in_stock', 'incoming', 'allocated', 'deployed', 'in_transit', 'decommissioned'];

const INVENTREE_SCHEMA = [
  { field: 'part_number',   label: 'Part #',    type: 'text',   required: false },
  { field: 'name',          label: 'Name',      type: 'text',   required: true  },
  { field: 'quantity',      label: 'Qty',       type: 'number', required: false },
  { field: 'serial_number', label: 'Serial',    type: 'text',   required: false },
  { field: 'status',        label: 'Status',    type: 'status', required: false },
  { field: 'location_name', label: 'Location',  type: 'text',   required: false },
  { field: 'purchase_price',label: 'Price',     type: 'money',  required: false },
  { field: 'notes',         label: 'Notes',     type: 'text',   required: false },
];

const INVENTREE_SKIPPED = [
  'Part ID', 'Batch', 'Status Code', 'Location ID', 'Supplier Part ID', 'Supplier ID',
  'Supplier Name', 'Customer ID', 'Installed In', 'Build ID', 'Parent ID',
  'Sales Order ID', 'Purchase Order ID', 'Packaging', 'Link', 'Building',
  'Review Needed', 'Delete on Deplete', 'Last Updated', 'Stocktake', 'Expiry Date',
  'tags', 'consumed_by', 'stocktake_user', 'purchase_price_currency',
];

function mapInventreeRow(rawRow) {
  const normRow = {};
  for (const [k, v] of Object.entries(rawRow)) normRow[norm(k)] = v;

  // Parse "CODE | Description" from Part Name
  const partName = String(normRow['part name'] ?? '').trim();
  const pipeIdx  = partName.indexOf(' | ');
  const part_number = pipeIdx > -1 ? partName.slice(0, pipeIdx).trim() : '';
  const name        = pipeIdx > -1 ? partName.slice(pipeIdx + 3).trim() : partName;

  // Quantity: InvenTree exports as "263.00000" — keep 0 as-is (don't force min 1)
  const rawQty = parseFloat(String(normRow['quantity'] ?? ''));
  const quantity = String(isNaN(rawQty) ? 0 : Math.round(rawQty));

  // Status mapping
  const rawStatus = String(normRow['status'] ?? '').trim().toLowerCase();
  const status = INVENTREE_STATUS_MAP[rawStatus] || 'in_stock';

  return {
    part_number,
    name,
    quantity,
    serial_number:      String(normRow['serial']          ?? '').trim(),
    status,
    location_name:      String(normRow['location name']   ?? '').trim(),
    // Accept both 'purchase_price' (InvenTree internal) and 'purchase price' (our export header)
    purchase_price:     parseMoney(normRow['purchase_price'] ?? normRow['purchase price']),
    notes:              String(normRow['notes']            ?? '').trim(),
    _inventree_stock_id: String(normRow['stock item id']  ?? '').trim(),
  };
}

function inventreeIssues(row) {
  const issues = [];
  if (!row.name || !String(row.name).trim()) issues.push('Missing name');
  const qty = parseInt(String(row.quantity), 10);
  if (isNaN(qty) || qty < 1) issues.push('Invalid quantity');
  if (row.status && !VALID_INVENTORY_STATUSES.includes(row.status)) issues.push(`Unknown status: "${row.status}"`);
  return issues;
}

const VALID_SOW_STATUSES = ['Draft', 'Active', 'Issued', 'Complete', 'Cancelled', 'On Hold', 'Archived', 'Expired'];
const VALID_MS_STATUSES  = ['Awaiting', 'Ready to bill', 'Invoiced', 'Paid', 'Cancelled', 'Complete'];

// Legacy import: Expired is now a cancel reason, not a standalone status
const LEGACY_VALID_SOW_STATUSES = ['Draft', 'Active', 'Issued', 'Complete', 'Cancelled', 'On Hold', 'Archived'];
const CANCELLED_REASONS = ['Not Won', 'Event Cancelled', 'Revoked', 'Expired', 'Not Bidded'];

// Statuses in the legacy spreadsheet that should be remapped automatically
const LEGACY_STATUS_MAP = {
  // sub-cancelled statuses → Cancelled + reason
  'not won':                   { status: 'Cancelled', cancellation_reason: 'Not Won' },
  'event cancelled':           { status: 'Cancelled', cancellation_reason: 'Event Cancelled' },
  'revoked':                   { status: 'Cancelled', cancellation_reason: 'Revoked' },
  'expired':                   { status: 'Cancelled', cancellation_reason: 'Expired' },
  // new mappings requested
  'no bid':                    { status: 'Cancelled', cancellation_reason: 'Not Bidded' },
  'not bidded':                { status: 'Cancelled', cancellation_reason: 'Not Bidded' },
  'closed lost to competition':{ status: 'Cancelled', cancellation_reason: 'Not Won' },
  'lost to competition':       { status: 'Cancelled', cancellation_reason: 'Not Won' },
  'pending':                   { status: 'Draft',     cancellation_reason: null },
  // generic cancelled with no reason → default to Expired
  'cancelled':                 { status: 'Cancelled', cancellation_reason: 'Expired' },
  // other common names
  'lost':                      { status: 'Cancelled', cancellation_reason: 'Not Won' },
  'won':                       { status: 'Active',    cancellation_reason: null },
};

// ── Legacy Tracker — locked column schemas ────────────────────────────────────
const LEGACY_SOW_SCHEMA = [
  { csv: 'Reference',         field: 'reference',        label: 'Reference',     type: 'id',     required: true  },
  { csv: 'Status',            field: 'status',           label: 'Status',        type: 'status',        required: true  },
  { csv: '',                  field: 'cancellation_reason', label: 'Cancel Reason', type: 'cancel_reason', required: false },
  { csv: 'Client',            field: 'client',           label: 'Client',        type: 'text',   required: false },
  { csv: 'Detail',            field: 'detail',           label: 'Detail / Name', type: 'text',   required: false },
  { csv: 'Owner',             field: 'owner',            label: 'Owner (PM)',    type: 'text',   required: false },
  { csv: 'Type',              field: 'type',             label: 'Type',          type: 'text',   required: false },
  { csv: 'SOW Date',          field: 'sow_date',         label: 'SOW Date',      type: 'date',   required: false },
  { csv: 'Total excl VAT',    field: 'total_excl_vat',   label: 'Value',         type: 'money',  required: false },
  { csv: 'Source',            field: 'source',           label: 'Source',        type: 'text',   required: false },
  { csv: 'Date Received',     field: 'date_received',    label: 'Date Received', type: 'date',   required: false },
  { csv: 'Sourcing Deadline', field: 'sourcing_deadline',label: 'Deadline',      type: 'date',   required: false },
  { csv: 'Contact',           field: 'contact',          label: 'Contact',       type: 'text',   required: false },
  { csv: 'Issued Version',    field: 'issued_version',   label: 'Version',       type: 'number', required: false },
  { csv: 'Payment Terms',     field: 'payment_terms',    label: 'Pay Terms',     type: 'number', required: false },
  { csv: 'Sage Quote',        field: 'sage_quote',       label: 'Sage Quote',    type: 'text',   required: false },
  { csv: 'PO Number',         field: 'po_number',        label: 'PO Number',     type: 'text',   required: false },
  { csv: 'Notes',             field: 'notes',            label: 'Notes',         type: 'text',   required: false },
  { csv: 'Triage Notes',      field: 'triage_notes',     label: 'Triage Notes',  type: 'text',   required: false },
];

const LEGACY_MS_SCHEMA = [
  { csv: 'Reference',           field: 'sow_reference',       label: 'SOW Ref',       type: 'id',     required: true  },
  { csv: 'Milestone',           field: 'milestone_code',      label: 'Code',          type: 'text',   required: false },
  { csv: 'Status',              field: 'status',              label: 'Status',        type: 'status', required: false },
  { csv: 'Description',         field: 'description',         label: 'Description',   type: 'text',   required: false },
  { csv: 'Contract Date',       field: 'contract_date',       label: 'Contract Date', type: 'date',   required: false },
  { csv: 'Forecast Date',       field: 'forecast_date',       label: 'Forecast Date', type: 'date',   required: false },
  { csv: 'Invoice Amount excl', field: 'invoice_amount_excl', label: 'Amt excl VAT',  type: 'money',  required: false },
  { csv: 'Invoice Amount incl', field: 'invoice_amount_incl', label: 'Amt incl VAT',  type: 'money',  required: false },
  { csv: 'Customer PO',         field: 'customer_po',         label: 'Customer PO',   type: 'text',   required: false },
  { csv: 'Invoice Number',      field: 'invoice_number',      label: 'Invoice No',    type: 'text',   required: false },
  { csv: 'Formal Invoice Date', field: 'formal_invoice_date', label: 'Invoice Date',  type: 'date',   required: false },
  { csv: 'Payment Terms',       field: 'payment_terms',       label: 'Pay Terms',     type: 'number', required: false },
  { csv: 'Coupa Approved',      field: 'coupa_approved',      label: 'Coupa',         type: 'date',   required: false },
  { csv: 'Est Pay Date',        field: 'est_pay_date',        label: 'Est Pay',       type: 'date',   required: false },
  { csv: 'Paid',                field: 'paid_date',           label: 'Paid Date',     type: 'date',   required: false },
  { csv: 'Project Manager',     field: 'project_manager',     label: 'PM',            type: 'text',   required: false },
  { csv: 'Assigned To',         field: 'assigned_to',         label: 'Assigned To',   type: 'text',   required: false },
  { csv: 'Contact',             field: 'contact',             label: 'Contact',       type: 'text',   required: false },
  { csv: 'Delivery Comments',   field: 'delivery_comments',   label: 'Comments',      type: 'text',   required: false },
  { csv: 'Detail',              field: 'detail',              label: 'SOW Detail',    type: 'text',   required: false },
];

// Skipped calculated columns (displayed in mapping but not imported)
const LEGACY_SOW_SKIP = ['Milestones Sum', 'Calculated Invoiced Sum', 'Remaining to Invoice',
                         'Sourcing Event ID', 'Location'];

function parseExcelDate(v) {
  if (v == null || v === '') return '';
  const s = String(v).trim();
  if (!s || s === '0') return '';
  const uk = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (uk) { const yr = uk[3].length === 2 ? '20' + uk[3] : uk[3]; return `${yr}-${uk[2].padStart(2,'0')}-${uk[1].padStart(2,'0')}`; }
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const n = parseFloat(s);
  if (!isNaN(n) && n > 40000 && n < 60000) return new Date(Math.round((n - 25569) * 86400000)).toISOString().slice(0, 10);
  return s;
}
function parseMoney(v) {
  if (v == null || v === '') return '';
  const n = parseFloat(String(v).replace(/[£,\s]/g, ''));
  return isNaN(n) ? '' : String(n);
}
function fmtDate(s) {
  if (!s) return '';
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : s;
}
function fmtMoney(s) {
  if (!s) return '';
  const n = parseFloat(s);
  return isNaN(n) ? s : '£' + n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function mapLegacyRow(rawRow, schema) {
  const out = {};
  // Normalise raw row keys so "Client " matches "Client", "CLIENT" matches "Client", etc.
  const normRow = {};
  for (const [k, val] of Object.entries(rawRow)) normRow[norm(k)] = val;

  for (const col of schema) {
    if (!col.csv) { out[col.field] = ''; continue; } // derived fields
    let v = normRow[norm(col.csv)] ?? '';
    // Treat unresolved Excel formula strings as empty
    if (typeof v === 'string' && v.startsWith('=')) v = '';
    if (col.type === 'date')        v = parseExcelDate(v);
    else if (col.type === 'money')  v = parseMoney(v);
    else                            v = String(v).trim();
    out[col.field] = v;
  }
  // Auto-apply legacy status migration
  const rawStatus = (out.status || '').toLowerCase();
  const mapped = LEGACY_STATUS_MAP[rawStatus];
  if (mapped) {
    out.status = mapped.status;
    if (mapped.cancellation_reason !== null && !out.cancellation_reason) {
      out.cancellation_reason = mapped.cancellation_reason;
    }
  }
  // Any Cancelled row still missing a reason defaults to Expired
  if (out.status === 'Cancelled' && !out.cancellation_reason) {
    out.cancellation_reason = 'Expired';
  }
  return out;
}

function legacyIssues(row, schema, isSow) {
  const issues = [];
  const ref = row[isSow ? 'reference' : 'sow_reference'];
  if (!ref || !String(ref).trim()) issues.push('Missing reference');
  const status = String(row.status || '').trim();
  const validSt = isSow ? LEGACY_VALID_SOW_STATUSES : VALID_MS_STATUSES;
  if (status && !validSt.includes(status)) issues.push(`Unknown status: "${status}"`);
  if (isSow && status === 'Cancelled') {
    const cr = String(row.cancellation_reason || '').trim();
    if (cr && !CANCELLED_REASONS.includes(cr)) issues.push(`Unknown cancel reason: "${cr}"`);
    // missing reason is not flagged — import will default to Expired
  }
  return issues;
}

// ── Legacy Tracker Import component ─────────────────────────────────────────
function LegacyTrackerImport() {
  const [sheetType,      setSheetType]      = useState('sows');
  const [fileBuffer,     setFileBuffer]     = useState(null);
  const [sheetNames,     setSheetNames]     = useState([]);
  const [activeSheet,    setActiveSheet]    = useState('');
  const [fileName,       setFileName]       = useState('');
  const [rows,           setRows]           = useState([]); // mapped+parsed rows with _id, _excluded
  const [editCell,       setEditCell]       = useState(null); // {rowId, field}
  const [search,         setSearch]         = useState('');
  const [issuesOnly,     setIssuesOnly]     = useState(false);
  const [importing,      setImporting]      = useState(false);
  const [upsert,         setUpsert]         = useState(false);
  const [result,         setResult]         = useState(null);
  const inputRef = useRef();

  // Legacy delete state
  const [deleteLegacyType,    setDeleteLegacyType]    = useState(null); // 'sows' | 'milestones'
  const [deleteLegacyConfirm, setDeleteLegacyConfirm] = useState('');
  const [deletingLegacy,      setDeletingLegacy]      = useState(false);
  const [deleteResult,        setDeleteResult]        = useState(null);

  // Import progress state
  const [importProgress, setImportProgress] = useState(null); // { done, total }

  const handleDeleteLegacy = async () => {
    if (deleteLegacyConfirm !== 'DELETE') return;
    setDeletingLegacy(true);
    try {
      const res = await api.post(`import/${deleteLegacyType}/delete-legacy`, {});
      setDeleteResult({ type: deleteLegacyType, deleted: res.deleted });
    } catch (e) {
      setDeleteResult({ error: e?.data?.error || e.message });
    }
    setDeletingLegacy(false);
    setDeleteLegacyType(null);
    setDeleteLegacyConfirm('');
  };

  const DangerZone = () => (
    <div className="mt-6 border border-red-200 rounded-lg overflow-hidden">
      <div className="px-3 py-2 bg-red-50 border-b border-red-200 flex items-center gap-2">
        <span className="text-[10px] font-bold text-red-700">Danger Zone — Delete Legacy Import Data</span>
        <span className="text-[8px] text-red-400">Admin only · irreversible</span>
      </div>
      <div className="p-3 flex flex-col gap-2">
        {deleteResult && (
          <div className={`text-[9px] rounded px-3 py-2 mb-1 ${deleteResult.error ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'}`}>
            {deleteResult.error ? `Error: ${deleteResult.error}` : `Deleted ${deleteResult.deleted} legacy ${deleteResult.type}.`}
          </div>
        )}
        <div className="text-[9px] text-gray-500 mb-1">
          Use these buttons to wipe all data that was stamped as coming from the legacy import, so you can re-upload a cleansed spreadsheet.
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => { setDeleteLegacyType('sows'); setDeleteLegacyConfirm(''); setDeleteResult(null); }}
            className="px-3 py-1.5 text-[9px] font-semibold bg-white border border-red-300 text-red-600 rounded hover:bg-red-50">
            Delete All Legacy SOWs
          </button>
          <button onClick={() => { setDeleteLegacyType('milestones'); setDeleteLegacyConfirm(''); setDeleteResult(null); }}
            className="px-3 py-1.5 text-[9px] font-semibold bg-white border border-red-300 text-red-600 rounded hover:bg-red-50">
            Delete All Legacy Milestones
          </button>
        </div>
      </div>
      {/* Confirm modal */}
      {deleteLegacyType && (
        <Modal
          title={<span className="text-red-700">Delete All Legacy {deleteLegacyType === 'sows' ? 'SOWs' : 'Milestones'}?</span>}
          onClose={() => { if (!deletingLegacy) { setDeleteLegacyType(null); setDeleteLegacyConfirm(''); } }}
        >
            <div className="text-[10px] text-gray-600 mb-4">
              This will permanently delete every {deleteLegacyType === 'sows' ? 'SOW (and its milestones)' : 'milestone'} that was created via the legacy import. Type <strong>DELETE</strong> to confirm.
            </div>
            <input
              className="w-full border border-gray-300 rounded px-3 py-2 text-[11px] font-mono mb-4 focus:outline-none focus:border-red-400"
              placeholder="Type DELETE to confirm"
              value={deleteLegacyConfirm}
              onChange={e => setDeleteLegacyConfirm(e.target.value)}
            />
            {deletingLegacy && (
              <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden mb-3">
                <div className="h-full w-2/5 bg-red-500/70 rounded-full" style={{ animation: 'progress-sweep 1.4s ease-in-out infinite' }} />
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <button onClick={() => { setDeleteLegacyType(null); setDeleteLegacyConfirm(''); }}
                disabled={deletingLegacy}
                className="px-4 py-2 text-[10px] border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-40">Cancel</button>
              <button onClick={handleDeleteLegacy} disabled={deleteLegacyConfirm !== 'DELETE' || deletingLegacy}
                className="px-4 py-2 text-[10px] font-bold bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-40">
                {deletingLegacy ? 'Deleting…' : 'Delete'}
              </button>
            </div>
        </Modal>
      )}
    </div>
  );

  const schema = sheetType === 'sows' ? LEGACY_SOW_SCHEMA : LEGACY_MS_SCHEMA;
  const isSow  = sheetType === 'sows';

  const processRows = (rawRows, sch) =>
    rawRows
      .filter(r => Object.values(r).some(v => String(v).trim() !== ''))
      .map((r, i) => ({ _id: i, _excluded: false, ...mapLegacyRow(r, sch) }));

  const loadSheet = useCallback((buffer, sheetName, type) => {
    const sch = (type || sheetType) === 'sows' ? LEGACY_SOW_SCHEMA : LEGACY_MS_SCHEMA;
    // Pass schema csv headers as hints so auto-detection picks the right sheet
    const preferCols = sheetName ? [] : sch.filter(c => c.csv).map(c => c.csv);
    try {
      const { rows: raw, sheetNames: names, usedSheet } = parseBuffer(buffer, sheetName, preferCols);
      setSheetNames(names);
      setActiveSheet(usedSheet);
      setRows(processRows(raw, sch));
      setResult(null); setEditCell(null);
    } catch (e) { console.error(e); }
  }, [sheetType]);

  const handleFile = async (file) => {
    if (!file) return;
    setFileName(file.name); setRows([]); setResult(null);
    const buffer = await new Promise((res, rej) => {
      const fr = new FileReader();
      fr.onload = e => res(e.target.result);
      fr.onerror = () => rej(new Error('Read error'));
      fr.readAsArrayBuffer(file);
    });
    setFileBuffer(buffer);
    loadSheet(buffer, '', sheetType);
  };

  const handleTypeChange = (type) => {
    setSheetType(type); setRows([]); setResult(null);
    if (fileBuffer) loadSheet(fileBuffer, '', type); // '' = re-detect best sheet for new type
  };

  const updateCell = (rowId, field, value) =>
    setRows(prev => prev.map(r => r._id === rowId ? { ...r, [field]: value } : r));
  const toggleExclude = (rowId) =>
    setRows(prev => prev.map(r => r._id === rowId ? { ...r, _excluded: !r._excluded } : r));

  const getIssues = (row) => legacyIssues(row, schema, isSow);

  const visible = rows.filter(r => {
    if (issuesOnly && getIssues(r).length === 0) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return schema.some(col => String(r[col.field] || '').toLowerCase().includes(q));
  });

  const issueCount    = rows.filter(r => getIssues(r).length > 0).length;
  const excludedCount = rows.filter(r => r._excluded).length;
  const importRows    = rows.filter(r => !r._excluded);

  const BATCH_SIZE = 100;
  const handleImport = async () => {
    setImporting(true); setResult(null);
    const endpoint = isSow ? 'sows' : 'milestones';
    const allApiRows = importRows.map(r => {
      const out = {};
      for (const col of schema) { if (r[col.field] !== '' && r[col.field] != null) out[col.field] = r[col.field]; }
      return out;
    });
    const acc = { imported: 0, updated: 0, skipped: 0, created_clients: 0, errors: [], skipped_refs: [], skipped_items: [], new_clients: [] };
    setImportProgress({ done: 0, total: allApiRows.length });
    for (let i = 0; i < allApiRows.length; i += BATCH_SIZE) {
      try {
        const res = await api.post(`import/${endpoint}`, { rows: allApiRows.slice(i, i + BATCH_SIZE), upsert: isSow ? upsert : false });
        acc.imported += res.imported || 0;
        acc.updated += res.updated || 0;
        acc.skipped += res.skipped || 0;
        acc.created_clients += res.created_clients || 0;
        (res.errors || []).forEach(e => acc.errors.push(e));
        (res.skipped_refs || []).forEach(r => acc.skipped_refs.push(r));
        (res.skipped_items || []).forEach(r => acc.skipped_items.push(r));
        (res.new_clients || []).forEach(c => acc.new_clients.push(c));
      } catch (e) { acc.errors.push({ error: e?.data?.error || e.message }); }
      setImportProgress({ done: Math.min(i + BATCH_SIZE, allApiRows.length), total: allApiRows.length });
    }
    setResult(acc);
    setImportProgress(null);
    setImporting(false);
  };

  // ── Column mapping display (shown always at top) ─────────────────────────
  const MappingTable = () => (
    <div className="mb-5 border border-gray-200 rounded-lg overflow-hidden">
      <div className="px-3 py-2 bg-navy text-white text-[10px] font-bold flex items-center justify-between">
        <span>Column Mapping — {isSow ? 'Opportunities Tracker' : 'Milestones'}</span>
        <span className="text-white/40 font-normal text-[8px]">locked to this spreadsheet format</span>
      </div>
      <div className="divide-y divide-gray-100">
        {/* Header row */}
        <div className="grid grid-cols-3 px-3 py-1.5 bg-gray-50 text-[8px] font-bold uppercase text-gray-400">
          <span>Spreadsheet Column</span>
          <span>→ App Field</span>
          <span>Type</span>
        </div>
        {schema.filter(col => col.csv).map(col => (
          <div key={col.field} className="grid grid-cols-3 px-3 py-1.5 text-[9px] hover:bg-gray-50/80">
            <div className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
              <span className="font-medium text-gray-700">{col.csv}</span>
              {col.required && <span className="text-[7px] text-red-500 font-bold ml-1">required</span>}
            </div>
            <div className="font-mono text-navy font-semibold">{col.field}</div>
            <div className="text-gray-400">{col.type}</div>
          </div>
        ))}
        {isSow && LEGACY_SOW_SKIP.length > 0 && (
          <div className="px-3 py-2 bg-gray-50/80">
            <div className="text-[8px] text-gray-400 font-medium mb-1">Skipped (calculated / not imported):</div>
            <div className="flex flex-wrap gap-1">
              {LEGACY_SOW_SKIP.map(s => (
                <span key={s} className="flex items-center gap-1 text-[8px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-300 shrink-0" />{s}
                </span>
              ))}
            </div>
          </div>
        )}
        {isSow && (
          <div className="px-3 py-2 bg-amber-50/60 border-t border-amber-100">
            <div className="text-[8px] text-amber-700 font-semibold mb-1.5">Status auto-migration (applied on upload):</div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1">
              {Object.entries(LEGACY_STATUS_MAP).map(([from, to]) => (
                <div key={from} className="flex items-center gap-1.5 text-[8px]">
                  <span className="font-mono bg-amber-100 text-amber-800 px-1 py-0.5 rounded capitalize">{from}</span>
                  <span className="text-gray-400">→</span>
                  <span className="font-semibold text-gray-700">Cancelled</span>
                  {to.cancellation_reason && (
                    <span className="text-gray-400 italic">/ {to.cancellation_reason}</span>
                  )}
                  {to.status !== 'Cancelled' && (
                    <span className="font-semibold text-emerald-700">{to.status}</span>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-2 text-[8px] text-amber-600">
              Cancelled SOWs require a reason: {CANCELLED_REASONS.join(' · ')}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // ── No file loaded — show upload screen ──────────────────────────────────
  if (rows.length === 0) {
    return (
      <div>
        <div className="mb-4">
          <div className="text-[11px] font-semibold text-navy mb-0.5">Legacy Tracker Import</div>
          <div className="text-[9px] text-gray-500">Upload the Alton Valley Opportunity Tracker Excel file. Columns are locked to the format below — review and edit any values before importing.</div>
        </div>

        <div className="flex gap-2 mb-5">
          {[['sows','Opportunities (SOWs)'],['milestones','Milestones']].map(([id, label]) => (
            <button key={id} onClick={() => handleTypeChange(id)}
              className={`px-4 py-2 text-[10px] font-semibold rounded border transition-colors ${sheetType === id ? 'bg-navy text-white border-navy' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
              {label}
            </button>
          ))}
        </div>

        <MappingTable />

        <div
          className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-navy transition-colors mb-4"
          onDragOver={e => e.preventDefault()}
          onDrop={e => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
          onClick={() => inputRef.current?.click()}
        >
          <input ref={inputRef} type="file" accept=".csv,.xlsx,.xls" className="hidden"
            onChange={e => handleFile(e.target.files[0])} />
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-8 h-8 mx-auto mb-2 text-gray-400">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"/>
          </svg>
          <div className="text-[10px] text-gray-500">{fileName || 'Drop your Excel file here or click to browse'}</div>
        </div>

        {sheetNames.length > 1 && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-semibold text-gray-600">Sheet:</span>
            {sheetNames.map(n => (
              <button key={n} onClick={() => { setActiveSheet(n); loadSheet(fileBuffer, n, sheetType); }}
                className={`px-2.5 py-1 text-[9px] font-semibold rounded border transition-colors ${activeSheet === n ? 'bg-navy text-white border-navy' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
                {n}
              </button>
            ))}
          </div>
        )}
        <DangerZone />
      </div>
    );
  }

  // ── File loaded — review & cleanse grid ─────────────────────────────────
  return (
    <div>
      {/* Summary bar */}
      <div className="flex items-center gap-4 mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200 flex-wrap">
        <div className="text-[10px]"><span className="text-xl font-extrabold text-navy">{rows.length}</span><span className="text-gray-500 ml-1">rows loaded</span></div>
        {issueCount > 0 && <div className="text-[10px]"><span className="text-xl font-extrabold text-amber-600">{issueCount}</span><span className="text-amber-600 ml-1">with issues</span></div>}
        {excludedCount > 0 && <div className="text-[10px]"><span className="text-xl font-extrabold text-gray-400">{excludedCount}</span><span className="text-gray-400 ml-1">excluded</span></div>}
        {importing && importProgress
          ? (
            <div className="flex-1 min-w-[140px]">
              <div className="flex justify-between text-[8px] text-gray-500 mb-0.5">
                <span>Importing…</span>
                <span>{importProgress.done} / {importProgress.total} rows</span>
              </div>
              <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div className="h-full bg-navy rounded-full transition-all duration-300" style={{ width: `${Math.max(2, (importProgress.done / importProgress.total) * 100)}%` }} />
              </div>
            </div>
          )
          : <div className="text-[10px] flex-1 text-right"><span className="text-xl font-extrabold text-emerald-600">{importRows.length}</span><span className="text-emerald-600 ml-1">will import</span></div>
        }
        {isSow && (
          <label className="flex items-center gap-1.5 text-[10px] text-gray-600 cursor-pointer select-none whitespace-nowrap">
            <input type="checkbox" checked={upsert} onChange={e => setUpsert(e.target.checked)} className="rounded border-gray-300" />
            Update existing
          </label>
        )}
        <button onClick={handleImport} disabled={importing || importRows.length === 0}
          className="px-4 py-2 bg-navy text-white text-[10px] font-bold rounded hover:bg-navy/80 disabled:opacity-50 whitespace-nowrap">
          {importing ? 'Importing…' : `Import ${importRows.length} rows →`}
        </button>
        <button onClick={() => { setRows([]); setFileName(''); setResult(null); }}
          className="px-3 py-2 border border-gray-200 text-[10px] rounded hover:bg-gray-50 text-gray-600">
          ← New file
        </button>
      </div>

      {/* Import result */}
      {result && (
        <div className={`mb-4 rounded p-4 border ${result.error ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'}`}>
          {result.error ? (
            <div className="text-[10px] text-red-700">{result.error}</div>
          ) : (
            <>
              <div className="flex gap-6 mb-2">
                {[
                  { label: 'Imported',          value: result.imported,        cls: 'text-emerald-700' },
                  ...(result.updated > 0  ? [{ label: 'Updated',      value: result.updated,  cls: 'text-blue-600'   }] : []),
                  { label: result.skipped > 0 ? 'Skipped (exists)' : 'Skipped', value: result.skipped, cls: 'text-gray-500' },
                  ...(result.created_clients > 0 ? [{ label: 'New Clients', value: result.created_clients, cls: 'text-blue-600' }] : []),
                ].map(s => (
                  <div key={s.label} className="text-center">
                    <div className={`text-2xl font-extrabold ${s.cls}`}>{s.value}</div>
                    <div className="text-[8px] uppercase font-bold text-gray-500">{s.label}</div>
                  </div>
                ))}
              </div>
              {(result.skipped_refs?.length > 0 || result.skipped_items?.length > 0) && (
                <div className="border border-gray-200 bg-gray-50 rounded p-2 mb-2">
                  <div className="text-[9px] font-bold text-gray-600 mb-1">
                    {result.skipped_refs?.length > 0 ? `${result.skipped_refs.length} already existed (not imported):` : `${result.skipped_items?.length} already existed (not imported):`}
                  </div>
                  <div className="flex flex-wrap gap-1 max-h-28 overflow-y-auto">
                    {(result.skipped_refs || []).map((ref, i) => (
                      <span key={i} className="text-[8px] font-mono bg-white border border-gray-200 text-gray-600 px-1.5 py-0.5 rounded">{ref}</span>
                    ))}
                    {(result.skipped_items || []).map((it, i) => (
                      <span key={i} className="text-[8px] font-mono bg-white border border-gray-200 text-gray-600 px-1.5 py-0.5 rounded">{it.ref} / {it.code}</span>
                    ))}
                  </div>
                </div>
              )}
              {result.errors?.length > 0 && (
                <div className="border border-red-200 bg-red-50 rounded p-2">
                  <div className="text-[9px] font-bold text-red-700 mb-1">{result.errors.length} rows failed</div>
                  <div className="space-y-0.5 max-h-28 overflow-y-auto">
                    {result.errors.map((e, i) => <div key={i} className="text-[8px] text-red-600">{e.ref || `Row ${e.row}`}: {e.error}</div>)}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Sheet selector (when file has multiple sheets) */}
      {sheetNames.length > 1 && (
        <div className="flex items-center gap-1.5 mb-3">
          <span className="text-[10px] font-semibold text-gray-500">Sheet:</span>
          {sheetNames.map(n => (
            <button key={n} onClick={() => { setActiveSheet(n); loadSheet(fileBuffer, n, sheetType); }}
              className={`px-2.5 py-0.5 text-[9px] font-semibold rounded border transition-colors ${activeSheet === n ? 'bg-navy text-white border-navy' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
              {n}
            </button>
          ))}
        </div>
      )}

      {/* Filter bar */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search any value…"
          className="border border-gray-200 rounded px-2 py-1 text-[10px] focus:outline-none focus:border-navy w-48" />
        <label className="flex items-center gap-1.5 text-[10px] text-amber-700 cursor-pointer select-none">
          <input type="checkbox" checked={issuesOnly} onChange={e => setIssuesOnly(e.target.checked)} className="rounded border-gray-300" />
          Issues only ({issueCount})
        </label>
        {issueCount > 0 && (
          <button onClick={() => setRows(prev => prev.map(r => getIssues(r).length > 0 ? { ...r, _excluded: true } : r))}
            className="px-2.5 py-1 text-[9px] text-red-600 border border-red-200 rounded hover:bg-red-50">
            Exclude all with issues
          </button>
        )}
        <span className="text-[9px] text-gray-400 ml-auto">Click any cell to edit · uncheck to exclude from import</span>
      </div>

      {/* Editable grid */}
      <div className="border border-gray-200 rounded">
        <div className="overflow-x-auto overflow-y-auto max-h-[55vh]">
          <table className="min-w-max w-full text-[9px]">
            <thead className="sticky top-0 z-10 bg-gray-50">
              <tr className="border-b border-gray-200">
                <th className="px-2 py-2 w-8 shrink-0 text-center">
                  <span className="text-[7px] text-gray-400 uppercase">Incl.</span>
                </th>
                <th className="px-1 py-2 text-gray-400 text-left w-8">#</th>
                {schema.map(col => (
                  <th key={col.field} className="px-2 py-2 text-left whitespace-nowrap border-l border-gray-100">
                    <div className="font-bold text-gray-600 text-[9px]">{col.label}</div>
                    <div className="text-[7px] text-gray-400 font-normal font-mono">{col.field}</div>
                  </th>
                ))}
                <th className="px-2 py-2 text-left whitespace-nowrap text-[8px] font-bold text-gray-400 uppercase border-l border-gray-100">Issues</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((row, vi) => {
                const issues = getIssues(row);
                const hasIssue = issues.length > 0;
                const validSt = isSow ? LEGACY_VALID_SOW_STATUSES : VALID_MS_STATUSES;
                const isCancelledRow = isSow && row.status === 'Cancelled';
                return (
                  <tr key={row._id}
                    className={`border-b border-gray-100 transition-colors ${row._excluded ? 'opacity-35 bg-gray-50 line-through' : hasIssue ? 'bg-amber-50/50' : 'hover:bg-blue-50/20'}`}>
                    <td className="px-2 py-1 text-center">
                      <input type="checkbox" checked={!row._excluded} onChange={() => toggleExclude(row._id)} className="rounded border-gray-300" />
                    </td>
                    <td className="px-1 py-1 text-gray-300 text-right">{vi + 1}</td>
                    {schema.map(col => {
                      const val = row[col.field] ?? '';
                      const isEditing = editCell?.rowId === row._id && editCell?.field === col.field;
                      const isCancelReason = col.type === 'cancel_reason';
                      const notApplicable = isCancelReason && !isCancelledRow;
                      const isMissing = isCancelReason && isCancelledRow && !val;
                      const isBadReason = isCancelReason && isCancelledRow && val && !CANCELLED_REASONS.includes(val);
                      const isStatusBad = col.type === 'status' && val && !validSt.includes(val);
                      const isMissed = col.required && !val;
                      return (
                        <td key={col.field}
                          className={`px-1 py-0.5 border-l border-gray-100 ${notApplicable ? 'bg-gray-50/60' : ''}`}
                          onClick={() => !row._excluded && !notApplicable && setEditCell({ rowId: row._id, field: col.field })}>
                          {isEditing && !notApplicable ? (
                            (col.type === 'status') ? (
                              <select autoFocus value={val}
                                onChange={e => updateCell(row._id, col.field, e.target.value)}
                                onBlur={() => setEditCell(null)}
                                className="border border-navy rounded px-1 py-0.5 text-[9px] bg-white focus:outline-none">
                                <option value="">—</option>
                                {validSt.map(s => <option key={s} value={s}>{s}</option>)}
                              </select>
                            ) : isCancelReason ? (
                              <select autoFocus value={val}
                                onChange={e => updateCell(row._id, col.field, e.target.value)}
                                onBlur={() => setEditCell(null)}
                                className="border border-navy rounded px-1 py-0.5 text-[9px] bg-white focus:outline-none">
                                <option value="">— select —</option>
                                {CANCELLED_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                              </select>
                            ) : (
                              <input autoFocus value={val}
                                onChange={e => updateCell(row._id, col.field, e.target.value)}
                                onBlur={() => setEditCell(null)}
                                onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Escape') setEditCell(null); }}
                                className="border border-navy rounded px-1 py-0.5 text-[9px] w-full min-w-[70px] focus:outline-none bg-white" />
                            )
                          ) : notApplicable ? (
                            <span className="text-gray-200 text-[8px] px-1">—</span>
                          ) : (
                            <div
                              title={col.type === 'date' ? val : String(val)}
                              className={`px-1 py-0.5 rounded cursor-pointer hover:bg-white hover:ring-1 hover:ring-blue-300 truncate max-w-[130px] ${
                                isMissed || isMissing ? 'text-red-400 italic text-[8px]' :
                                isBadReason          ? 'text-red-600 font-bold' :
                                isStatusBad          ? 'text-red-600 font-bold' :
                                col.type === 'id'           ? 'font-mono font-semibold text-navy' :
                                col.type === 'date'         ? 'font-mono text-blue-700' :
                                col.type === 'money'        ? 'text-emerald-700 font-semibold' :
                                col.type === 'status'       ? 'font-semibold text-gray-700' :
                                isCancelReason && val       ? 'text-[8px] font-semibold text-orange-700 bg-orange-50 rounded px-1' :
                                'text-gray-700'
                              }`}>
                              {(isMissed || isMissing) ? '⚠ required' :
                               col.type === 'date'   ? fmtDate(val) :
                               col.type === 'money'  ? fmtMoney(val) :
                               val || <span className="text-gray-300">—</span>}
                            </div>
                          )}
                        </td>
                      );
                    })}
                    <td className="px-2 py-1 border-l border-gray-100">
                      {issues.length > 0 && (
                        <div className="flex flex-col gap-0.5">
                          {issues.map((iss, i) => (
                            <span key={i} className="text-[7px] bg-amber-100 text-amber-800 px-1 py-0.5 rounded whitespace-nowrap">{iss}</span>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {visible.length === 0 && (
                <tr><td colSpan={schema.length + 3} className="text-center py-8 text-gray-400 text-[10px]">No rows match your filter</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="px-3 py-1.5 border-t bg-gray-50 flex items-center justify-between text-[8px] text-gray-400">
          <span>Showing {visible.length} of {rows.length} rows</span>
          <span>Click a cell to edit in-place · status columns show a dropdown · Enter or Escape to confirm</span>
        </div>
      </div>
      <DangerZone />
    </div>
  );
}

function mapRow(rawRow, colMap) {
  const out = {};
  for (const [rawKey, val] of Object.entries(rawRow)) {
    const mapped = colMap[norm(rawKey)];
    if (mapped) out[mapped] = val;
  }
  return out;
}

function detectHeaders(rawRows, colMap) {
  if (!rawRows.length) return [];
  return Object.keys(rawRows[0]).map(h => ({ csv: h, db: colMap[norm(h)] || null }));
}

function sheetHeaderCols(ws) {
  if (!ws || !ws['!ref']) return [];
  const range = XLSX.utils.decode_range(ws['!ref']);
  // Limit to the header row only so we don't parse every cell in every sheet
  const headerWs = { ...ws, '!ref': XLSX.utils.encode_range({ s: range.s, e: { r: range.s.r, c: range.e.c } }) };
  const [headers = []] = XLSX.utils.sheet_to_json(headerWs, { header: 1, defval: '' });
  return headers.map(h => norm(String(h)));
}

function parseBuffer(buffer, sheetName, preferColumns = []) {
  const wb = XLSX.read(new Uint8Array(buffer), { type: 'array', cellDates: false });
  let name = sheetName;
  if (!name || !wb.Sheets[name]) {
    if (preferColumns.length > 0) {
      // Pick the sheet whose headers best match the expected schema columns
      const normPrefer = preferColumns.map(h => norm(h));
      let bestSheet = null, bestScore = -1;
      for (const n of wb.SheetNames) {
        const cols = sheetHeaderCols(wb.Sheets[n]);
        if (!cols.length) continue;
        const score = normPrefer.filter(c => cols.includes(c)).length;
        if (score > bestScore) { bestScore = score; bestSheet = n; }
      }
      name = bestSheet || wb.SheetNames[0];
    } else {
      name = wb.SheetNames.find(n => sheetHeaderCols(wb.Sheets[n]).length > 0) || wb.SheetNames[0];
    }
  }
  const ws = wb.Sheets[name];
  return { rows: XLSX.utils.sheet_to_json(ws, { defval: '' }), sheetNames: wb.SheetNames, usedSheet: name };
}

// ── Import tab ────────────────────────────────────────────────────────────────
function ImportTab({ title, description, endpoint, colMap, allowUpdate = false }) {
  const [rawRows,        setRawRows]        = useState(null);
  const [headers,        setHeaders]        = useState([]);
  const [fileName,       setFileName]       = useState('');
  const [loading,        setLoading]        = useState(false);
  const [importing,      setImporting]      = useState(false);
  const [importProgress, setImportProgress] = useState(null); // { done, total }
  const [result,         setResult]         = useState(null);
  const [error,          setError]          = useState(null);
  const [sheetNames,     setSheetNames]     = useState([]);
  const [activeSheet,    setActiveSheet]    = useState('');
  const [fileBuffer,     setFileBuffer]     = useState(null);
  const [upsertMode,     setUpsertMode]     = useState(false);
  const inputRef = useRef();

  const loadSheet = (buffer, sheetName) => {
    try {
      const { rows, sheetNames: names, usedSheet } = parseBuffer(buffer, sheetName);
      const filtered = rows.filter(r => Object.values(r).some(v => String(v).trim() !== ''));
      setRawRows(filtered);
      setHeaders(detectHeaders(filtered, colMap));
      setSheetNames(names);
      setActiveSheet(usedSheet);
    } catch (e) { setError('Could not parse file: ' + e.message); }
  };

  const handleFile = async (file) => {
    if (!file) return;
    setError(null); setResult(null); setRawRows(null); setSheetNames([]); setActiveSheet('');
    setFileName(file.name);
    setLoading(true);
    try {
      const buffer = await new Promise((res, rej) => {
        const fr = new FileReader();
        fr.onload = e => res(e.target.result);
        fr.onerror = () => rej(new Error('Read error'));
        fr.readAsArrayBuffer(file);
      });
      setFileBuffer(buffer);
      loadSheet(buffer, '');
    } catch (e) { setError('Could not read file: ' + e.message); }
    setLoading(false);
  };

  const handleSheetChange = (name) => {
    if (!fileBuffer) return;
    setError(null); setResult(null);
    loadSheet(fileBuffer, name);
  };

  const IMP_BATCH = 100;
  const handleImport = async () => {
    if (!rawRows?.length) return;
    setImporting(true); setError(null); setResult(null);
    const allRows = rawRows.map(r => mapRow(r, colMap));
    const acc = { imported: 0, updated: 0, skipped: 0, errors: [], created_clients: 0, new_clients: [], skipped_refs: [], skipped_items: [] };
    setImportProgress({ done: 0, total: allRows.length });
    for (let i = 0; i < allRows.length; i += IMP_BATCH) {
      try {
        const res = await api.post(`import/${endpoint}`, { rows: allRows.slice(i, i + IMP_BATCH), ...(upsertMode ? { upsert: true } : {}) });
        acc.imported += res.imported || 0;
        acc.updated += res.updated || 0;
        acc.skipped += res.skipped || 0;
        acc.created_clients += res.created_clients || 0;
        (res.errors || []).forEach(e => acc.errors.push(e));
        (res.new_clients || []).forEach(c => acc.new_clients.push(c));
        (res.skipped_refs || []).forEach(r => acc.skipped_refs.push(r));
        (res.skipped_items || []).forEach(r => acc.skipped_items.push(r));
      } catch (e) { setError(e?.data?.error || e.message || 'Import failed'); break; }
      setImportProgress({ done: Math.min(i + IMP_BATCH, allRows.length), total: allRows.length });
    }
    setResult(acc);
    setImportProgress(null);
    setImporting(false);
  };

  const mappedCount  = headers.filter(h => h.db).length;
  const previewRows  = rawRows?.slice(0, 8) || [];
  const previewCols  = headers.filter(h => h.db).slice(0, 8);

  return (
    <div>
      <div className="mb-4">
        <div className="text-[11px] font-semibold text-navy mb-0.5">{title}</div>
        <div className="text-[9px] text-gray-500">{description}</div>
      </div>

      <div
        className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-navy transition-colors mb-4"
        onDragOver={e => e.preventDefault()}
        onDrop={e => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
        onClick={() => inputRef.current?.click()}
      >
        <input ref={inputRef} type="file" accept=".csv,.xlsx,.xls" className="hidden"
          onChange={e => handleFile(e.target.files[0])} />
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-8 h-8 mx-auto mb-2 text-gray-400">
          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"/>
        </svg>
        {fileName
          ? <div className="text-[10px] font-semibold text-navy">{fileName}</div>
          : <div className="text-[10px] text-gray-500">Drop a CSV or Excel file here, or click to browse</div>}
      </div>

      {loading && <div className="text-[10px] text-gray-500 mb-4">Parsing file…</div>}

      {/* Sheet picker — shown when the file has multiple sheets */}
      {sheetNames.length > 1 && (
        <div className="mb-4 flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-semibold text-gray-600">Sheet:</span>
          {sheetNames.map(n => (
            <button key={n} onClick={() => handleSheetChange(n)}
              className={`px-2.5 py-1 text-[9px] font-semibold rounded border transition-colors ${activeSheet === n ? 'bg-navy text-white border-navy' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
              {n}
            </button>
          ))}
        </div>
      )}

      {error   && <div className="mb-4 bg-red-50 border border-red-200 rounded p-3 text-[10px] text-red-700">{error}</div>}

      {rawRows && !loading && (
        <div className="mb-4">
          <div className="text-[10px] font-bold text-gray-700 mb-2">
            Found {rawRows.length} rows · {mappedCount}/{headers.length} columns mapped
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-1.5 mb-3">
            {headers.map(h => (
              <div key={h.csv} className={`flex items-center gap-1.5 px-2 py-1 rounded text-[9px] ${h.db ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-gray-50 text-gray-400 border border-gray-200'}`}>
                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${h.db ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                <span className="truncate font-medium">{h.csv}</span>
                {h.db && <span className="text-emerald-600 shrink-0">→ {h.db}</span>}
              </div>
            ))}
          </div>

          {previewRows.length > 0 && previewCols.length > 0 && (
            <div className="overflow-x-auto border border-gray-200 rounded mb-4">
              <table className="min-w-max text-[9px]">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    {previewCols.map(h => (
                      <th key={h.db} className="px-2 py-1.5 text-left font-bold text-gray-500 uppercase whitespace-nowrap">{h.csv}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((row, i) => (
                    <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                      {previewCols.map(h => (
                        <td key={h.db} className="px-2 py-1 truncate max-w-[120px] text-gray-700">{String(row[h.csv] ?? '')}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {rawRows.length > 8 && (
                <div className="px-3 py-1.5 text-[8px] text-gray-400 border-t border-gray-100 bg-gray-50">
                  Showing first 8 of {rawRows.length} rows
                </div>
              )}
            </div>
          )}

          {allowUpdate && (
            <label className="flex items-center gap-1.5 mb-2 cursor-pointer w-fit">
              <input type="checkbox" checked={upsertMode} onChange={e => setUpsertMode(e.target.checked)} className="w-3 h-3 accent-navy" />
              <span className="text-[9px] text-gray-600">Update existing records (re-import to fix values)</span>
            </label>
          )}
          <div className="flex items-center gap-3 flex-wrap">
            <button onClick={handleImport} disabled={importing || !rawRows.length}
              className="px-4 py-2 bg-navy text-white text-[10px] font-bold rounded hover:bg-navy/80 disabled:opacity-50">
              {importing ? 'Importing…' : `Import ${rawRows.length} rows`}
            </button>
            <button onClick={() => { setRawRows(null); setFileName(''); setResult(null); setError(null); }}
              className="px-3 py-2 border border-gray-200 text-[10px] rounded hover:bg-gray-50 text-gray-600">
              Clear
            </button>
            {importing && importProgress && (
              <div className="flex-1 min-w-[140px]">
                <div className="flex justify-between text-[8px] text-gray-500 mb-0.5">
                  <span>Importing…</span>
                  <span>{importProgress.done} / {importProgress.total} rows</span>
                </div>
                <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-navy rounded-full transition-all duration-300" style={{ width: `${Math.max(2, (importProgress.done / importProgress.total) * 100)}%` }} />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {result && (
        <div className="mt-4 border border-emerald-200 bg-emerald-50 rounded p-4">
          <div className="text-[11px] font-bold text-emerald-800 mb-2">Import complete</div>
          <div className="flex gap-4 mb-2 flex-wrap">
            <div className="text-center">
              <div className="text-lg font-extrabold text-emerald-700">{result.imported}</div>
              <div className="text-[8px] text-emerald-600 uppercase font-bold">Imported</div>
            </div>
            {result.updated > 0 && (
              <div className="text-center">
                <div className="text-lg font-extrabold text-blue-600">{result.updated}</div>
                <div className="text-[8px] text-blue-600 uppercase font-bold">Updated</div>
              </div>
            )}
            {(result.skipped_refs?.length > 0) && (
              <div className="text-center">
                <div className="text-lg font-extrabold text-amber-600">{result.skipped_refs.length}</div>
                <div className="text-[8px] text-amber-600 uppercase font-bold">Already existed</div>
              </div>
            )}
            {(result.skipped_empty > 0 || (result.skipped > 0 && !result.skipped_refs)) && (
              <div className="text-center">
                <div className="text-lg font-extrabold text-gray-400">{result.skipped_empty ?? result.skipped}</div>
                <div className="text-[8px] text-gray-400 uppercase font-bold">Blank rows</div>
              </div>
            )}
            {result.created_clients > 0 && (
              <div className="text-center">
                <div className="text-lg font-extrabold text-blue-600">{result.created_clients}</div>
                <div className="text-[8px] text-blue-600 uppercase font-bold">New Clients</div>
              </div>
            )}
            {result.new_suppliers?.length > 0 && (
              <div className="text-center">
                <div className="text-lg font-extrabold text-purple-600">{result.new_suppliers.length}</div>
                <div className="text-[8px] text-purple-600 uppercase font-bold">New Suppliers</div>
              </div>
            )}
          </div>
          {result.new_clients?.length > 0 && (
            <div className="text-[9px] text-blue-700 mb-2">New clients created: {result.new_clients.join(', ')}</div>
          )}
          {result.new_suppliers?.length > 0 && (
            <div className="text-[9px] text-purple-700 mb-2">New suppliers auto-created: {result.new_suppliers.join(', ')}</div>
          )}
          {result.skipped_refs?.length > 0 && (
            <div className="mt-2 border border-amber-200 bg-amber-50 rounded p-2">
              <div className="text-[9px] font-bold text-amber-700 mb-1">
                {result.skipped_refs.length} already existed — not imported:
              </div>
              <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                {result.skipped_refs.map((ref, i) => (
                  <span key={i} className="text-[8px] font-mono bg-white border border-amber-200 text-amber-800 px-1.5 py-0.5 rounded">{ref}</span>
                ))}
              </div>
            </div>
          )}
          {(result.skipped_items?.length > 0) && (
            <div className="mt-2 border border-gray-200 bg-gray-50 rounded p-2">
              <div className="text-[9px] font-bold text-gray-600 mb-1">Already existed (skipped):</div>
              <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                {result.skipped_items.map((it, i) => (
                  <span key={i} className="text-[8px] font-mono bg-white border border-gray-200 text-gray-600 px-1.5 py-0.5 rounded">{it.ref} / {it.code}</span>
                ))}
              </div>
            </div>
          )}
          {result.errors?.length > 0 && (
            <div className="mt-2 border border-red-200 bg-red-50 rounded p-2">
              <div className="text-[9px] font-bold text-red-700 mb-1">{result.errors.length} error{result.errors.length > 1 ? 's' : ''}</div>
              <div className="space-y-0.5 max-h-40 overflow-y-auto">
                {result.errors.map((e, i) => (
                  <div key={i} className="text-[8px] text-red-600">
                    {typeof e === 'string' ? e : `${e.ref || `Row ${e.row}`}${e.code ? ` / ${e.code}` : ''}: ${e.error}`}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Cleanse tab ───────────────────────────────────────────────────────────────
function SortIcon({ active, dir }) {
  if (!active) return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="inline w-2.5 h-2.5 ml-0.5 text-gray-300">
      <path d="M7 15l5 5 5-5M7 9l5-5 5 5"/>
    </svg>
  );
  return dir === 'asc' ? (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="inline w-2.5 h-2.5 ml-0.5 text-navy">
      <path d="M7 15l5 5 5-5"/>
    </svg>
  ) : (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="inline w-2.5 h-2.5 ml-0.5 text-navy">
      <path d="M7 9l5-5 5 5"/>
    </svg>
  );
}

function sortRows(rows, col, dir) {
  if (!col) return rows;
  return [...rows].sort((a, b) => {
    let av = a[col] ?? '';
    let bv = b[col] ?? '';
    if (typeof av === 'number' || typeof bv === 'number') {
      av = Number(av) || 0;
      bv = Number(bv) || 0;
    } else {
      av = String(av).toLowerCase();
      bv = String(bv).toLowerCase();
    }
    if (av < bv) return dir === 'asc' ? -1 : 1;
    if (av > bv) return dir === 'asc' ?  1 : -1;
    return 0;
  });
}

function CleanseTab() {
  const [subTab,     setSubTab]     = useState('sows');
  const [sows,       setSows]       = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [clients,    setClients]    = useState([]);
  const [pos,        setPos]        = useState([]);
  const [hwRequests, setHwRequests] = useState([]);
  const [inventory,  setInventory]  = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [search,     setSearch]     = useState('');
  const [selected,   setSelected]   = useState(new Set());
  const [deleting,        setDeleting]        = useState(false);
  const [deleteProgress,  setDeleteProgress]  = useState(null); // { done, total }
  const [confirm,         setConfirm]         = useState(false);
  const [result,          setResult]          = useState(null);
  const [sort,            setSort]            = useState({ col: null, dir: 'asc' });
  const [mergeInto,       setMergeInto]       = useState('');

  const cycleSort = (col) => setSort(prev =>
    prev.col === col
      ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { col, dir: 'asc' }
  );

  const loadSows = useCallback(async () => {
    setLoading(true);
    try { setSows(await api.get('sows')); }
    catch { setSows([]); }
    setLoading(false);
  }, []);

  const loadMilestones = useCallback(async () => {
    setLoading(true);
    try { setMilestones(await api.get('milestones')); }
    catch { setMilestones([]); }
    setLoading(false);
  }, []);

  const loadClients = useCallback(async () => {
    setLoading(true);
    try { setClients(await api.get('import/clients')); }
    catch { setClients([]); }
    setLoading(false);
  }, []);

  const loadPos = useCallback(async () => {
    setLoading(true);
    try { setPos(await api.get('purchase-orders')); }
    catch { setPos([]); }
    setLoading(false);
  }, []);

  const loadHwRequests = useCallback(async () => {
    setLoading(true);
    try { setHwRequests(await api.get('hardware-requests')); }
    catch { setHwRequests([]); }
    setLoading(false);
  }, []);

  const loadInventory = useCallback(async () => {
    setLoading(true);
    try { setInventory(await api.get('import/inventory')); }
    catch { setInventory([]); }
    setLoading(false);
  }, []);

  useEffect(() => {
    setSelected(new Set());
    setSearch('');
    setResult(null);
    setConfirm(false);
    setSort({ col: null, dir: 'asc' });
    setMergeInto('');
    if (subTab === 'sows') loadSows();
    else if (subTab === 'milestones') loadMilestones();
    else if (subTab === 'pos') loadPos();
    else if (subTab === 'hardware-requests') loadHwRequests();
    else if (subTab === 'inventory') loadInventory();
    else loadClients();
  }, [subTab, loadSows, loadMilestones, loadClients, loadPos, loadHwRequests, loadInventory]);

  const rows     = subTab === 'sows' ? sows : subTab === 'milestones' ? milestones : subTab === 'pos' ? pos : subTab === 'hardware-requests' ? hwRequests : subTab === 'inventory' ? inventory : clients;
  const filtered = sortRows(rows.filter(r => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    if (subTab === 'sows') {
      return (r.reference || '').toLowerCase().includes(q)
          || (r.detail    || '').toLowerCase().includes(q)
          || (r.client_name || '').toLowerCase().includes(q)
          || (r.status    || '').toLowerCase().includes(q)
          || (r.owner_name || '').toLowerCase().includes(q);
    }
    if (subTab === 'clients') {
      return (r.name || '').toLowerCase().includes(q)
          || (r.code || '').toLowerCase().includes(q);
    }
    if (subTab === 'pos') {
      return (r.po_number      || '').toLowerCase().includes(q)
          || (r.supplier_name  || '').toLowerCase().includes(q)
          || (r.status         || '').toLowerCase().includes(q)
          || (r.notes          || '').toLowerCase().includes(q);
    }
    if (subTab === 'hardware-requests') {
      return (r.product_name   || '').toLowerCase().includes(q)
          || (r.requester_name || '').toLowerCase().includes(q)
          || (r.status         || '').toLowerCase().includes(q)
          || (r.catalog_code   || '').toLowerCase().includes(q);
    }
    if (subTab === 'inventory') {
      return (r.name          || '').toLowerCase().includes(q)
          || (r.part_number   || '').toLowerCase().includes(q)
          || (r.asset_tag     || '').toLowerCase().includes(q)
          || (r.status        || '').toLowerCase().includes(q)
          || (r.location_name || '').toLowerCase().includes(q);
    }
    return (r.reference      || '').toLowerCase().includes(q)
        || (r.milestone_code || '').toLowerCase().includes(q)
        || (r.description    || '').toLowerCase().includes(q)
        || (r.status         || '').toLowerCase().includes(q);
  }), sort.col, sort.dir);

  const allVisibleSelected = filtered.length > 0 && filtered.every(r => selected.has(r.id));

  const toggleAll = () => {
    if (allVisibleSelected) {
      setSelected(prev => { const n = new Set(prev); filtered.forEach(r => n.delete(r.id)); return n; });
    } else {
      setSelected(prev => { const n = new Set(prev); filtered.forEach(r => n.add(r.id)); return n; });
    }
  };

  const toggle = (id) => setSelected(prev => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const DELETE_CHUNK = 100;
  const handleDelete = async () => {
    setDeleting(true); setConfirm(false); setResult(null);
    const allIds = [...selected];
    let totalDeleted = 0;
    let errorMsg = null;
    setDeleteProgress({ done: 0, total: allIds.length });
    for (let i = 0; i < allIds.length; i += DELETE_CHUNK) {
      const chunk = allIds.slice(i, i + DELETE_CHUNK);
      try {
        const body = { ids: chunk };
        if (subTab === 'clients' && mergeInto) body.merge_into = mergeInto;
        const res = await api.post(`import/${subTab}/bulk-delete`, body);
        totalDeleted += res.deleted || 0;
      } catch (e) {
        errorMsg = e?.data?.error || e.message;
        break;
      }
      setDeleteProgress({ done: Math.min(i + DELETE_CHUNK, allIds.length), total: allIds.length });
    }
    setDeleteProgress(null);
    if (errorMsg) {
      setResult({ error: errorMsg });
    } else {
      setResult({ deleted: totalDeleted, merged: subTab === 'clients' && !!mergeInto });
      setSelected(new Set()); setMergeInto('');
      if (subTab === 'sows') loadSows();
      else if (subTab === 'milestones') loadMilestones();
      else if (subTab === 'pos') loadPos();
      else if (subTab === 'hardware-requests') loadHwRequests();
      else if (subTab === 'inventory') loadInventory();
      else loadClients();
    }
    setDeleting(false);
  };

  const isInvalidStatus = (status) => subTab === 'sows' && !VALID_SOW_STATUSES.includes(status);

  return (
    <div>
      <div className="mb-4">
        <div className="text-[11px] font-semibold text-navy mb-0.5">Data Cleanse</div>
        <div className="text-[9px] text-gray-500">
          View all records unfiltered. Select and bulk-delete bad rows, then re-import with corrected data.
          Records with an invalid status are highlighted.
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-1 mb-4 border-b border-gray-200">
        {[{ id: 'sows', label: 'SOWs' }, { id: 'milestones', label: 'Milestones' }, { id: 'clients', label: 'Clients' }, { id: 'pos', label: 'Purchase Orders' }, { id: 'hardware-requests', label: 'Hardware Requests' }, { id: 'inventory', label: 'Inventory' }].map(t => (
          <button key={t.id} onClick={() => setSubTab(t.id)}
            className={`px-3 py-1.5 text-[9px] font-semibold border-b-2 -mb-px transition-colors ${subTab === t.id ? 'border-navy text-navy' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {t.label} {!loading && subTab === t.id && <span className="ml-1 text-gray-400">({rows.length})</span>}
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <input
          type="text"
          placeholder={subTab === 'sows' ? 'Search reference, detail, client, status, owner…' : subTab === 'milestones' ? 'Search reference, milestone code, status…' : subTab === 'pos' ? 'Search PO number, supplier, status…' : subTab === 'hardware-requests' ? 'Search product, requester, status…' : subTab === 'inventory' ? 'Search name, part #, asset tag, location, status…' : 'Search client name…'}
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="flex-1 min-w-[200px] text-[10px] border border-gray-200 rounded px-2.5 py-1.5 focus:outline-none focus:border-navy"
        />
        <span className="text-[9px] text-gray-500 whitespace-nowrap">
          {filtered.length} record{filtered.length !== 1 ? 's' : ''}
          {search && ` (filtered from ${rows.length})`}
        </span>
        {selected.size > 0 && subTab === 'clients' && (
          <select
            value={mergeInto}
            onChange={e => setMergeInto(e.target.value)}
            className="text-[10px] border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:border-navy bg-white"
          >
            <option value="">— Delete (unlink SOWs) —</option>
            {clients.filter(c => !selected.has(c.id)).map(c => (
              <option key={c.id} value={c.id}>{c.name}{c.sow_count > 0 ? ` (${c.sow_count} SOWs)` : ''}</option>
            ))}
          </select>
        )}
        {selected.size > 0 && (
          <button
            onClick={() => setConfirm(true)}
            disabled={deleting}
            className="px-3 py-1.5 bg-red-600 text-white text-[10px] font-bold rounded hover:bg-red-700 disabled:opacity-50 whitespace-nowrap"
          >
            {deleting ? 'Deleting…' : subTab === 'clients' && mergeInto ? `Merge selected (${selected.size})` : `Delete selected (${selected.size})`}
          </button>
        )}
        {selected.size > 0 && (
          <button onClick={() => { setSelected(new Set()); setMergeInto(''); }}
            className="px-2.5 py-1.5 border border-gray-200 text-[10px] rounded hover:bg-gray-50 text-gray-600 whitespace-nowrap">
            Clear selection
          </button>
        )}
      </div>

      {/* Confirm / progress */}
      {(confirm || deleting) && (
        <div className="mb-3 bg-red-50 border border-red-300 rounded p-3">
          <div className="flex items-center gap-3">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4 text-red-600 flex-shrink-0">
              <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            <span className="text-[10px] text-red-700 flex-1">
              {deleting ? <>Deleting <strong>{selected.size}</strong> record{selected.size > 1 ? 's' : ''}…</>
                : subTab === 'clients' && mergeInto
                ? <>Merge <strong>{selected.size}</strong> client{selected.size > 1 ? 's' : ''} into <strong>{clients.find(c => c.id === mergeInto)?.name}</strong>? All SOWs, contacts and projects will be reassigned. This cannot be undone.</>
                : subTab === 'clients'
                ? <>Permanently delete <strong>{selected.size}</strong> client{selected.size > 1 ? 's' : ''}? All linked SOWs will be unlinked. This cannot be undone.</>
                : subTab === 'pos'
                ? <>Permanently delete <strong>{selected.size}</strong> purchase order{selected.size > 1 ? 's' : ''}? PO lines will be removed. This cannot be undone.</>
                : subTab === 'hardware-requests'
                ? <>Permanently delete <strong>{selected.size}</strong> hardware request{selected.size > 1 ? 's' : ''}? This cannot be undone.</>
                : subTab === 'inventory'
                ? <>Permanently delete <strong>{selected.size}</strong> inventory item{selected.size > 1 ? 's' : ''}? This cannot be undone.</>
                : <>Permanently delete <strong>{selected.size}</strong> {subTab === 'sows' ? 'SOW' : 'milestone'} record{selected.size > 1 ? 's' : ''}? This cannot be undone.</>
              }
            </span>
            {!deleting && <button onClick={handleDelete} className="px-3 py-1 bg-red-600 text-white text-[10px] font-bold rounded hover:bg-red-700">Confirm Delete</button>}
            {!deleting && <button onClick={() => setConfirm(false)} className="px-3 py-1 border border-gray-200 bg-white text-[10px] rounded hover:bg-gray-50">Cancel</button>}
          </div>
          {deleting && deleteProgress && (
            <div className="mt-2">
              <div className="flex justify-between text-[8px] text-red-600 mb-0.5">
                <span>Deleting…</span>
                <span>{deleteProgress.done} / {deleteProgress.total}</span>
              </div>
              <div className="h-1.5 bg-red-200 rounded-full overflow-hidden">
                <div className="h-full bg-red-500 rounded-full transition-all duration-300" style={{ width: `${Math.max(2, (deleteProgress.done / deleteProgress.total) * 100)}%` }} />
              </div>
            </div>
          )}
        </div>
      )}

      {result && (
        <div className={`mb-3 rounded p-2.5 text-[10px] ${result.error ? 'bg-red-50 border border-red-200 text-red-700' : 'bg-emerald-50 border border-emerald-200 text-emerald-700'}`}>
          {result.error ? `Error: ${result.error}` : result.merged ? `Merged ${result.deleted} client${result.deleted !== 1 ? 's' : ''} successfully.` : `Deleted ${result.deleted} record${result.deleted !== 1 ? 's' : ''} successfully.`}
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-[10px] text-gray-400">Loading…</div>
      ) : (
        <div className="border border-gray-200 rounded">
          <div className="overflow-x-auto overflow-y-auto max-h-[60vh]">
            {subTab === 'hardware-requests' ? (
              <table className="min-w-max w-full text-[9px]">
                <thead className="sticky top-0 bg-gray-50 z-10">
                  <tr className="border-b border-gray-200">
                    <th className="px-2 py-2 text-center w-8">
                      <input type="checkbox" checked={allVisibleSelected} onChange={toggleAll} className="rounded border-gray-300" />
                    </th>
                    {[
                      { key: 'product_name',   label: 'Product',    cls: 'text-left' },
                      { key: 'catalog_code',   label: 'Code',       cls: 'text-left whitespace-nowrap' },
                      { key: 'requester_name', label: 'Requested By', cls: 'text-left whitespace-nowrap' },
                      { key: 'status',         label: 'Status',     cls: 'text-left whitespace-nowrap' },
                      { key: 'quantity',       label: 'Qty',        cls: 'text-right whitespace-nowrap' },
                      { key: 'total_cost',     label: 'Cost',       cls: 'text-right whitespace-nowrap' },
                      { key: 'created_at',     label: 'Raised',     cls: 'text-left whitespace-nowrap' },
                    ].map(({ key, label, cls }) => (
                      <th key={key} onClick={() => cycleSort(key)}
                        className={`px-2 py-2 font-bold text-gray-500 uppercase cursor-pointer select-none hover:text-navy ${cls}`}>
                        {label}<SortIcon active={sort.col === key} dir={sort.dir} />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(r => (
                    <tr key={r.id} onClick={() => toggle(r.id)}
                      className={`border-b border-gray-100 cursor-pointer transition-colors ${selected.has(r.id) ? 'bg-red-50' : 'hover:bg-gray-50'}`}>
                      <td className="px-2 py-1.5 text-center" onClick={e => e.stopPropagation()}>
                        <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} className="rounded border-gray-300" />
                      </td>
                      <td className="px-2 py-1.5 font-semibold text-navy max-w-[180px] truncate">{r.product_name || '—'}</td>
                      <td className="px-2 py-1.5 font-mono text-gray-500 whitespace-nowrap">{r.catalog_code || '—'}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap text-gray-600">{r.requester_name || '—'}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap">
                        <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold ${
                          r.status === 'approved'     ? 'bg-emerald-100 text-emerald-700'
                          : r.status === 'rejected'   ? 'bg-red-100 text-red-700'
                          : r.status === 'submitted'  ? 'bg-blue-100 text-blue-700'
                          : r.status === 'ordered'    ? 'bg-purple-100 text-purple-700'
                          : r.status === 'delivered'  ? 'bg-emerald-100 text-emerald-800'
                          : 'bg-gray-100 text-gray-600'
                        }`}>{r.status}</span>
                      </td>
                      <td className="px-2 py-1.5 text-right whitespace-nowrap text-gray-600">{r.quantity ?? '—'}</td>
                      <td className="px-2 py-1.5 text-right whitespace-nowrap text-gray-700">
                        {r.total_cost ? `£${Number(r.total_cost).toLocaleString()}` : '—'}
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap text-gray-500">{r.created_at ? r.created_at.slice(0, 10) : '—'}</td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr><td colSpan={8} className="text-center py-8 text-gray-400">No hardware requests found</td></tr>
                  )}
                </tbody>
              </table>
            ) : subTab === 'inventory' ? (
              <table className="min-w-max w-full text-[9px]">
                <thead className="sticky top-0 bg-gray-50 z-10">
                  <tr className="border-b border-gray-200">
                    <th className="px-2 py-2 text-center w-8">
                      <input type="checkbox" checked={allVisibleSelected} onChange={toggleAll} className="rounded border-gray-300" />
                    </th>
                    {[
                      { key: 'asset_tag',     label: 'Asset Tag',  cls: 'text-left whitespace-nowrap' },
                      { key: 'part_number',   label: 'Part #',     cls: 'text-left whitespace-nowrap' },
                      { key: 'name',          label: 'Name',       cls: 'text-left' },
                      { key: 'quantity',      label: 'Qty',        cls: 'text-right whitespace-nowrap' },
                      { key: 'status',        label: 'Status',     cls: 'text-left whitespace-nowrap' },
                      { key: 'location_name', label: 'Location',   cls: 'text-left whitespace-nowrap' },
                      { key: 'import_source', label: 'Source',     cls: 'text-left whitespace-nowrap' },
                    ].map(({ key, label, cls }) => (
                      <th key={key} onClick={() => cycleSort(key)}
                        className={`px-2 py-2 font-bold text-gray-500 uppercase cursor-pointer select-none hover:text-navy ${cls}`}>
                        {label}<SortIcon active={sort.col === key} dir={sort.dir} />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(r => (
                    <tr key={r.id} onClick={() => toggle(r.id)}
                      className={`border-b border-gray-100 cursor-pointer transition-colors ${selected.has(r.id) ? 'bg-red-50' : 'hover:bg-gray-50'}`}>
                      <td className="px-2 py-1.5 text-center" onClick={e => e.stopPropagation()}>
                        <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} className="rounded border-gray-300" />
                      </td>
                      <td className="px-2 py-1.5 font-mono text-[8px] text-navy whitespace-nowrap">{r.asset_tag || '—'}</td>
                      <td className="px-2 py-1.5 font-mono text-gray-500 text-[8px] whitespace-nowrap">{r.part_number || '—'}</td>
                      <td className="px-2 py-1.5 font-medium text-gray-800 max-w-[220px] truncate">{r.name}</td>
                      <td className="px-2 py-1.5 text-right text-gray-700 whitespace-nowrap">{r.quantity ?? 1}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap">
                        <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold ${
                          r.status === 'in_stock'       ? 'bg-emerald-100 text-emerald-700'
                          : r.status === 'allocated'    ? 'bg-amber-100 text-amber-700'
                          : r.status === 'deployed'     ? 'bg-purple-100 text-purple-700'
                          : r.status === 'incoming'     ? 'bg-blue-100 text-blue-700'
                          : r.status === 'in_transit'   ? 'bg-cyan-100 text-cyan-700'
                          : r.status === 'decommissioned' ? 'bg-gray-100 text-gray-400'
                          : 'bg-gray-100 text-gray-600'
                        }`}>{r.status}</span>
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap text-gray-600">{r.location_name || '—'}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap">
                        {r.import_source
                          ? <span className="px-1.5 py-0.5 rounded text-[7px] font-semibold bg-indigo-50 text-indigo-600 border border-indigo-100">{r.import_source}</span>
                          : <span className="text-gray-300 text-[8px]">manual</span>}
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr><td colSpan={8} className="text-center py-8 text-gray-400">No inventory items found</td></tr>
                  )}
                </tbody>
              </table>
            ) : subTab === 'pos' ? (
              <table className="min-w-max w-full text-[9px]">
                <thead className="sticky top-0 bg-gray-50 z-10">
                  <tr className="border-b border-gray-200">
                    <th className="px-2 py-2 text-center w-8">
                      <input type="checkbox" checked={allVisibleSelected} onChange={toggleAll} className="rounded border-gray-300" />
                    </th>
                    {[
                      { key: 'po_number',     label: 'PO Number',   cls: 'text-left whitespace-nowrap' },
                      { key: 'supplier_name', label: 'Supplier',    cls: 'text-left' },
                      { key: 'status',        label: 'Status',      cls: 'text-left whitespace-nowrap' },
                      { key: 'subtotal',      label: 'Value',       cls: 'text-right whitespace-nowrap' },
                      { key: 'order_date',    label: 'Date Issued', cls: 'text-left whitespace-nowrap' },
                      { key: 'notes',         label: 'Notes',       cls: 'text-left' },
                    ].map(({ key, label, cls }) => (
                      <th key={key} onClick={() => cycleSort(key)}
                        className={`px-2 py-2 font-bold text-gray-500 uppercase cursor-pointer select-none hover:text-navy ${cls}`}>
                        {label}<SortIcon active={sort.col === key} dir={sort.dir} />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(r => (
                    <tr key={r.id} onClick={() => toggle(r.id)}
                      className={`border-b border-gray-100 cursor-pointer transition-colors ${selected.has(r.id) ? 'bg-red-50' : 'hover:bg-gray-50'}`}>
                      <td className="px-2 py-1.5 text-center" onClick={e => e.stopPropagation()}>
                        <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} className="rounded border-gray-300" />
                      </td>
                      <td className="px-2 py-1.5 font-mono font-semibold text-navy whitespace-nowrap">{r.po_number}</td>
                      <td className="px-2 py-1.5 text-gray-600 max-w-[160px] truncate">{r.supplier_name || '—'}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap">
                        <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold ${
                          r.status === 'delivered' || r.status === 'closed' ? 'bg-emerald-100 text-emerald-700'
                          : r.status === 'issued' || r.status === 'acknowledged' ? 'bg-blue-100 text-blue-700'
                          : r.status === 'part_delivered' ? 'bg-amber-100 text-amber-700'
                          : r.status === 'cancelled' ? 'bg-red-100 text-red-700'
                          : 'bg-gray-100 text-gray-600'
                        }`}>{r.status}</span>
                      </td>
                      <td className="px-2 py-1.5 text-right whitespace-nowrap text-gray-700">
                        {r.subtotal != null && r.subtotal !== 0
                          ? `£${Number(r.subtotal).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                          : '—'}
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap text-gray-600">{r.order_date || '—'}</td>
                      <td className="px-2 py-1.5 max-w-[200px] truncate text-gray-500">{r.notes || '—'}</td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr><td colSpan={7} className="text-center py-8 text-gray-400">No purchase orders found</td></tr>
                  )}
                </tbody>
              </table>
            ) : subTab === 'clients' ? (
              <table className="min-w-max w-full text-[9px]">
                <thead className="sticky top-0 bg-gray-50 z-10">
                  <tr className="border-b border-gray-200">
                    <th className="px-2 py-2 text-center w-8">
                      <input type="checkbox" checked={allVisibleSelected} onChange={toggleAll} className="rounded border-gray-300" />
                    </th>
                    {[
                      { key: 'name',          label: 'Client Name',  cls: 'text-left' },
                      { key: 'code',          label: 'Code',         cls: 'text-left whitespace-nowrap' },
                      { key: 'sow_count',     label: 'SOWs',         cls: 'text-right whitespace-nowrap' },
                      { key: 'contact_count', label: 'Contacts',     cls: 'text-right whitespace-nowrap' },
                    ].map(({ key, label, cls }) => (
                      <th key={key} onClick={() => cycleSort(key)}
                        className={`px-2 py-2 font-bold text-gray-500 uppercase cursor-pointer select-none hover:text-navy ${cls}`}>
                        {label}<SortIcon active={sort.col === key} dir={sort.dir} />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(r => (
                    <tr key={r.id} onClick={() => toggle(r.id)}
                      className={`border-b border-gray-100 cursor-pointer transition-colors ${selected.has(r.id) ? 'bg-red-50' : 'hover:bg-gray-50'}`}>
                      <td className="px-2 py-1.5 text-center" onClick={e => e.stopPropagation()}>
                        <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} className="rounded border-gray-300" />
                      </td>
                      <td className="px-2 py-1.5 font-semibold text-navy">{r.name}</td>
                      <td className="px-2 py-1.5 text-gray-500 font-mono">{r.code || '—'}</td>
                      <td className="px-2 py-1.5 text-right">
                        {r.sow_count > 0
                          ? <span className="px-1.5 py-0.5 rounded text-[8px] font-bold bg-blue-100 text-blue-700">{r.sow_count}</span>
                          : <span className="text-gray-300">0</span>}
                      </td>
                      <td className="px-2 py-1.5 text-right text-gray-500">{r.contact_count || 0}</td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr><td colSpan={5} className="text-center py-8 text-gray-400">No clients found</td></tr>
                  )}
                </tbody>
              </table>
            ) : subTab === 'sows' ? (
              <table className="min-w-max w-full text-[9px]">
                <thead className="sticky top-0 bg-gray-50 z-10">
                  <tr className="border-b border-gray-200">
                    <th className="px-2 py-2 text-center w-8">
                      <input type="checkbox" checked={allVisibleSelected} onChange={toggleAll}
                        className="rounded border-gray-300" />
                    </th>
                    {[
                      { key: 'reference',     label: 'Reference',  cls: 'text-left whitespace-nowrap' },
                      { key: 'detail',        label: 'Detail',     cls: 'text-left' },
                      { key: 'status',        label: 'Status',     cls: 'text-left whitespace-nowrap' },
                      { key: 'client_name',   label: 'Client',     cls: 'text-left whitespace-nowrap' },
                      { key: 'owner_name',    label: 'Owner',      cls: 'text-left whitespace-nowrap' },
                      { key: 'total_excl_vat',label: 'Value',      cls: 'text-right whitespace-nowrap' },
                    ].map(({ key, label, cls }) => (
                      <th key={key} onClick={() => cycleSort(key)}
                        className={`px-2 py-2 font-bold text-gray-500 uppercase cursor-pointer select-none hover:text-navy ${cls}`}>
                        {label}<SortIcon active={sort.col === key} dir={sort.dir} />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(r => (
                    <tr key={r.id}
                      onClick={() => toggle(r.id)}
                      className={`border-b border-gray-100 cursor-pointer transition-colors ${selected.has(r.id) ? 'bg-red-50' : 'hover:bg-gray-50'}`}>
                      <td className="px-2 py-1.5 text-center" onClick={e => e.stopPropagation()}>
                        <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)}
                          className="rounded border-gray-300" />
                      </td>
                      <td className="px-2 py-1.5 font-mono font-semibold text-navy whitespace-nowrap">{r.reference}</td>
                      <td className="px-2 py-1.5 max-w-[200px] truncate text-gray-700">{r.detail}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap">
                        <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold ${
                          isInvalidStatus(r.status)
                            ? 'bg-red-100 text-red-700 border border-red-300'
                            : r.status === 'Active'  ? 'bg-emerald-100 text-emerald-700'
                            : r.status === 'Draft'   ? 'bg-gray-100 text-gray-600'
                            : r.status === 'Issued'  ? 'bg-blue-100 text-blue-700'
                            : r.status === 'Complete' ? 'bg-purple-100 text-purple-700'
                            : 'bg-amber-100 text-amber-700'
                        }`}>{r.status}</span>
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap text-gray-600">{r.client_name || '—'}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap text-gray-600">{r.owner_name || '—'}</td>
                      <td className="px-2 py-1.5 text-right whitespace-nowrap text-gray-700">
                        {r.total_excl_vat ? `£${Number(r.total_excl_vat).toLocaleString()}` : '—'}
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr><td colSpan={7} className="text-center py-8 text-gray-400">No records found</td></tr>
                  )}
                </tbody>
              </table>
            ) : (
              <table className="min-w-max w-full text-[9px]">
                <thead className="sticky top-0 bg-gray-50 z-10">
                  <tr className="border-b border-gray-200">
                    <th className="px-2 py-2 text-center w-8">
                      <input type="checkbox" checked={allVisibleSelected} onChange={toggleAll}
                        className="rounded border-gray-300" />
                    </th>
                    {[
                      { key: 'reference',           label: 'SOW Ref',      cls: 'text-left whitespace-nowrap' },
                      { key: 'milestone_code',      label: 'Milestone',    cls: 'text-left whitespace-nowrap' },
                      { key: 'status',              label: 'Status',       cls: 'text-left whitespace-nowrap' },
                      { key: 'description',         label: 'Description',  cls: 'text-left' },
                      { key: 'contract_date',       label: 'Contract Date',cls: 'text-left whitespace-nowrap' },
                      { key: 'invoice_amount_excl', label: 'Amount excl',  cls: 'text-right whitespace-nowrap' },
                    ].map(({ key, label, cls }) => (
                      <th key={key} onClick={() => cycleSort(key)}
                        className={`px-2 py-2 font-bold text-gray-500 uppercase cursor-pointer select-none hover:text-navy ${cls}`}>
                        {label}<SortIcon active={sort.col === key} dir={sort.dir} />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(r => (
                    <tr key={r.id}
                      onClick={() => toggle(r.id)}
                      className={`border-b border-gray-100 cursor-pointer transition-colors ${selected.has(r.id) ? 'bg-red-50' : 'hover:bg-gray-50'}`}>
                      <td className="px-2 py-1.5 text-center" onClick={e => e.stopPropagation()}>
                        <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)}
                          className="rounded border-gray-300" />
                      </td>
                      <td className="px-2 py-1.5 font-mono font-semibold text-navy whitespace-nowrap">{r.reference}</td>
                      <td className="px-2 py-1.5 font-mono whitespace-nowrap">{r.milestone_code}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap">
                        <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold ${
                          r.status === 'Paid'           ? 'bg-emerald-100 text-emerald-700'
                          : r.status === 'Invoiced'     ? 'bg-blue-100 text-blue-700'
                          : r.status === 'Ready to bill'? 'bg-amber-100 text-amber-700'
                          : r.status === 'Awaiting'     ? 'bg-gray-100 text-gray-600'
                          : r.status === 'Cancelled'    ? 'bg-red-100 text-red-700'
                          : 'bg-red-100 text-red-700 border border-red-300'
                        }`}>{r.status}</span>
                      </td>
                      <td className="px-2 py-1.5 max-w-[200px] truncate text-gray-700">{r.description || '—'}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap text-gray-600">{r.contract_date || '—'}</td>
                      <td className="px-2 py-1.5 text-right whitespace-nowrap text-gray-700">
                        {r.invoice_amount_excl ? `£${Number(r.invoice_amount_excl).toLocaleString()}` : '—'}
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr><td colSpan={7} className="text-center py-8 text-gray-400">No records found</td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
          {filtered.length > 0 && (
            <div className="px-3 py-1.5 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
              <span className="text-[8px] text-gray-400">{selected.size} of {filtered.length} selected</span>
              <button onClick={toggleAll} className="text-[8px] text-navy hover:underline">
                {allVisibleSelected ? 'Deselect all visible' : 'Select all visible'}
              </button>
            </div>
          )}
        </div>
      )}

      <OrphanCleanup />
      <DataResetPanel />
    </div>
  );
}

function OrphanCleanup() {
  const [running,    setRunning]    = useState(false);
  const [result,     setResult]     = useState(null);
  const [expanded,   setExpanded]   = useState(false);

  const run = async () => {
    if (!window.confirm('Scan the database for orphaned rows and delete / null them out. This is safe and reversible only via a fresh import. Continue?')) return;
    setRunning(true);
    setResult(null);
    try {
      const r = await api.post('cleanse/orphans', {});
      setResult(r);
    } catch (e) {
      setResult({ error: e?.data?.error || e.message });
    }
    setRunning(false);
  };

  return (
    <div className="mt-6 border border-amber-200 rounded-lg overflow-hidden">
      <button
        className="w-full px-3 py-2 bg-amber-50 border-b border-amber-200 flex items-center justify-between"
        onClick={() => setExpanded(p => !p)}
      >
        <span className="text-[10px] font-bold text-amber-800">Orphan Data Cleanup</span>
        <span className="text-[8px] text-amber-500">Safe to run anytime {expanded ? '▲' : '▼'}</span>
      </button>
      {expanded && (
        <div className="p-3 space-y-2">
          <p className="text-[9px] text-gray-500">
            Scans every table for rows whose parent record no longer exists — e.g. milestones for deleted SOWs,
            payment links for deleted milestones, inventory movements for deleted items — and removes them.
            Nullable FK columns (e.g. task → milestone link) are NULLed rather than deleted.
          </p>
          {result && !result.error && (
            <div className="bg-emerald-50 border border-emerald-200 rounded px-3 py-2 text-[9px] text-emerald-700">
              {result.fixed === 0
                ? '✓ No orphaned rows found — database is clean.'
                : `✓ Fixed ${result.fixed} orphaned row(s).`}
              {result.fixed > 0 && Object.keys(result.details).length > 0 && (
                <ul className="mt-1.5 space-y-0.5">
                  {Object.entries(result.details).map(([k, n]) => (
                    <li key={k} className="font-mono text-[8px]">{k}: {n}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {result?.error && (
            <div className="bg-red-50 border border-red-200 rounded px-3 py-2 text-[9px] text-red-700">
              Error: {result.error}
            </div>
          )}
          <button
            onClick={run}
            disabled={running}
            className="px-4 py-1.5 text-[10px] font-bold bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-40"
          >
            {running ? 'Scanning…' : '🔍 Find & fix orphaned rows'}
          </button>
        </div>
      )}
    </div>
  );
}

function DataResetPanel() {
  const [expanded, setExpanded] = useState(false);
  const [states,   setStates]   = useState({});

  const run = async (target, label) => {
    if (!window.confirm(`Permanently delete ALL ${label}. This cannot be undone. Continue?`)) return;
    setStates(p => ({ ...p, [target]: { running: true, result: null } }));
    try {
      const r = await api.post('cleanse/truncate', { target });
      setStates(p => ({ ...p, [target]: { running: false, result: r } }));
    } catch (e) {
      setStates(p => ({ ...p, [target]: { running: false, result: { error: e?.data?.error || e.message } } }));
    }
  };

  const categories = [
    { key: 'pipeline',    label: 'Pipeline Data',   desc: 'All CRM opportunities' },
    { key: 'raid',        label: 'RAID Data',        desc: 'All risks, assumptions, issues and dependencies' },
    { key: 'tasks',       label: 'Tasks',            desc: 'All standalone tasks and task groups' },
    { key: 'forecast',    label: 'Forecast Data',    desc: 'All planner slots and budget forecast entries' },
    { key: 'audit_trail', label: 'Audit Trail',      desc: 'All audit log entries' },
  ];

  return (
    <div className="mt-4 border border-red-300 rounded-lg overflow-hidden">
      <button
        className="w-full px-3 py-2 bg-red-50 border-b border-red-200 flex items-center justify-between"
        onClick={() => setExpanded(p => !p)}
      >
        <span className="text-[10px] font-bold text-red-800">Bulk Data Reset</span>
        <span className="text-[8px] text-red-500">Admin only · irreversible {expanded ? '▲' : '▼'}</span>
      </button>
      {expanded && (
        <div className="p-3">
          <p className="text-[9px] text-gray-500 mb-3">
            Permanently wipe all rows in a category. Use for pre-live cleanup to remove data you don't want to carry forward.
          </p>
          <div className="divide-y divide-gray-100">
            {categories.map(c => {
              const s = states[c.key] || {};
              return (
                <div key={c.key} className="flex items-center justify-between py-2.5 gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-[9px] font-semibold text-gray-800">{c.label}</div>
                    <div className="text-[8px] text-gray-400">{c.desc}</div>
                    {s.result && !s.result.error && (
                      <div className="text-[8px] text-emerald-600 mt-0.5">✓ Deleted {s.result.deleted} {s.result.label}</div>
                    )}
                    {s.result?.error && (
                      <div className="text-[8px] text-red-600 mt-0.5">✗ {s.result.error}</div>
                    )}
                  </div>
                  <button
                    onClick={() => run(c.key, c.label.toLowerCase())}
                    disabled={s.running}
                    className="shrink-0 px-3 py-1.5 text-[9px] font-bold bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-40 transition-colors"
                  >
                    {s.running ? 'Deleting…' : 'Delete All'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── JSON full export import ───────────────────────────────────────────────────
function JsonImportSection() {
  const [fileName,    setFileName]    = useState('');
  const [jsonData,    setJsonData]    = useState(null);
  const [skipZero,    setSkipZero]    = useState(true);
  const [upsert,      setUpsert]      = useState(false);
  const [importing,   setImporting]   = useState(false);
  const [result,      setResult]      = useState(null);
  const inputRef = useRef();

  const handleFile = async (file) => {
    if (!file) return;
    setFileName(file.name); setResult(null); setJsonData(null);
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      if (!parsed.stock) throw new Error('Not a valid InvenTree full export (missing "stock" key)');
      setJsonData(parsed);
    } catch (e) {
      setResult({ error: e.message });
    }
  };

  const handleImport = async () => {
    if (!jsonData) return;
    setImporting(true); setResult(null);
    try {
      const res = await api.post('import/inventory/inventree-json', {
        data: jsonData, upsert, skipZeroQty: skipZero,
      });
      setResult(res);
    } catch (e) {
      setResult({ error: e?.data?.error || e.message });
    }
    setImporting(false);
  };

  const stockCount  = jsonData?.stock?.length ?? 0;
  const zeroCount   = jsonData ? (jsonData.stock || []).filter(s => parseFloat(s.quantity) === 0).length : 0;
  const importCount = jsonData ? stockCount - (skipZero ? zeroCount : 0) : 0;

  return (
    <div className="border border-navy/20 rounded-lg overflow-hidden mb-4">
      <div className="px-3 py-2 bg-navy text-white text-[10px] font-bold flex items-center justify-between">
        <span>Import from InvenTree Full JSON Export</span>
        <span className="text-white/50 font-normal text-[9px]">Recommended — includes categories, pricing, rich notes</span>
      </div>
      <div className="p-4">
        <div className="text-[9px] text-gray-500 mb-3">
          Run the full export script in your browser console on assets.altonvalley.com to get
          <code className="mx-1 px-1 bg-gray-100 rounded text-[9px]">inventree_full_export.json</code>,
          then upload it here. Categories, locations, pricing and legacy notes are all mapped automatically.
        </div>

        {/* Drop zone */}
        <div
          className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors mb-3 ${
            jsonData ? 'border-emerald-300 bg-emerald-50' : 'border-gray-300 hover:border-navy'
          }`}
          onDragOver={e => e.preventDefault()}
          onDrop={e => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
          onClick={() => inputRef.current?.click()}
        >
          <input ref={inputRef} type="file" accept=".json" className="hidden"
            onChange={e => handleFile(e.target.files[0])} />
          {jsonData ? (
            <div className="text-[10px] text-emerald-700 font-semibold">
              ✓ {fileName} — {stockCount} stock items, {(jsonData.parts||[]).length} parts, {(jsonData.categories||[]).length} categories
            </div>
          ) : (
            <>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-7 h-7 mx-auto mb-1.5 text-gray-400">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"/>
              </svg>
              <div className="text-[10px] text-gray-500">{fileName || 'Drop inventree_full_export.json here, or click to browse'}</div>
            </>
          )}
        </div>

        {/* Controls */}
        {jsonData && (
          <div className="flex items-center gap-3 flex-wrap">
            <label className="flex items-center gap-1.5 text-[10px] text-gray-600 cursor-pointer select-none">
              <input type="checkbox" checked={skipZero} onChange={e => setSkipZero(e.target.checked)} className="rounded border-gray-300" />
              Skip zero-quantity ({zeroCount})
            </label>
            <label className="flex items-center gap-1.5 text-[10px] text-gray-600 cursor-pointer select-none">
              <input type="checkbox" checked={upsert} onChange={e => setUpsert(e.target.checked)} className="rounded border-gray-300" />
              Update existing (by InvenTree ID)
            </label>
            <div className="flex-1" />
            <span className="text-[10px] text-emerald-700 font-semibold">{importCount} rows to import</span>
            <button
              onClick={handleImport}
              disabled={importing || importCount === 0}
              className="px-4 py-2 bg-navy text-white text-[10px] font-bold rounded hover:bg-navy/80 disabled:opacity-50 whitespace-nowrap"
            >
              {importing ? 'Importing…' : `Import ${importCount} items →`}
            </button>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className={`mt-3 rounded p-3 border text-[10px] ${result.error ? 'bg-red-50 border-red-200 text-red-700' : 'bg-emerald-50 border-emerald-200'}`}>
            {result.error ? result.error : (
              <>
                <div className="flex gap-5 mb-1 flex-wrap">
                  {[
                    { label: 'Imported', v: result.imported, cls: 'text-emerald-700' },
                    ...(result.updated  > 0 ? [{ label: 'Updated',       v: result.updated,              cls: 'text-blue-600'   }] : []),
                    ...(result.skipped  > 0 ? [{ label: 'Skipped',       v: result.skipped,              cls: 'text-gray-500'   }] : []),
                    ...(result.new_locations?.length > 0 ? [{ label: 'New locations', v: result.new_locations.length, cls: 'text-purple-600' }] : []),
                  ].map(s => (
                    <div key={s.label} className="text-center">
                      <div className={`text-xl font-extrabold ${s.cls}`}>{s.v}</div>
                      <div className="text-[8px] uppercase font-bold text-gray-500">{s.label}</div>
                    </div>
                  ))}
                </div>
                {result.new_locations?.length > 0 && (
                  <div className="text-[9px] text-purple-700">New locations: {result.new_locations.join(', ')}</div>
                )}
                {result.errors?.length > 0 && (
                  <div className="mt-2 text-[9px] text-red-600">{result.errors.length} rows failed — first: {result.errors[0]?.error}</div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Inventory import (InvenTree CSV) ─────────────────────────────────────────
function InventoryImportTab() {
  const [fileBuffer,     setFileBuffer]     = useState(null);
  const [sheetNames,     setSheetNames]     = useState([]);
  const [activeSheet,    setActiveSheet]    = useState('');
  const [fileName,       setFileName]       = useState('');
  const [rows,           setRows]           = useState([]);
  const [editCell,       setEditCell]       = useState(null);
  const [search,         setSearch]         = useState('');
  const [issuesOnly,     setIssuesOnly]     = useState(false);
  const [skipZeroQty,    setSkipZeroQty]    = useState(true);
  const [importing,      setImporting]      = useState(false);
  const [upsert,         setUpsert]         = useState(false);
  const [result,         setResult]         = useState(null);
  const [importProgress, setImportProgress] = useState(null);
  const [deleteLegacyConfirm, setDeleteLegacyConfirm] = useState('');
  const [showDeleteZone,      setShowDeleteZone]      = useState(false);
  const [deletingLegacy,      setDeletingLegacy]      = useState(false);
  const [deleteResult,        setDeleteResult]        = useState(null);
  const inputRef = useRef();

  const processRows = (rawRows) =>
    rawRows
      .filter(r => Object.values(r).some(v => String(v).trim() !== ''))
      .map((r, i) => ({ _id: i, _excluded: false, ...mapInventreeRow(r) }));

  const loadSheet = useCallback((buffer, sheetName) => {
    const preferCols = ['Part Name', 'Quantity', 'Serial', 'Status', 'Location Name'];
    try {
      const { rows: raw, sheetNames: names, usedSheet } = parseBuffer(buffer, sheetName, preferCols);
      setSheetNames(names);
      setActiveSheet(usedSheet);
      setRows(processRows(raw));
      setResult(null); setEditCell(null);
    } catch (e) { console.error(e); }
  }, []);

  const handleFile = async (file) => {
    if (!file) return;
    setFileName(file.name); setRows([]); setResult(null);
    const buffer = await new Promise((res, rej) => {
      const fr = new FileReader();
      fr.onload = e => res(e.target.result);
      fr.onerror = () => rej(new Error('Read error'));
      fr.readAsArrayBuffer(file);
    });
    setFileBuffer(buffer);
    loadSheet(buffer, '');
  };

  const updateCell = (rowId, field, value) =>
    setRows(prev => prev.map(r => r._id === rowId ? { ...r, [field]: value } : r));
  const toggleExclude = (rowId) =>
    setRows(prev => prev.map(r => r._id === rowId ? { ...r, _excluded: !r._excluded } : r));

  const getIssues = (row) => inventreeIssues(row);

  const visible = rows.filter(r => {
    if (issuesOnly && getIssues(r).length === 0) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return INVENTREE_SCHEMA.some(col => String(r[col.field] || '').toLowerCase().includes(q));
  });

  const issueCount    = rows.filter(r => getIssues(r).length > 0).length;
  const excludedCount = rows.filter(r => r._excluded).length;
  const zeroQtyCount  = rows.filter(r => parseInt(r.quantity, 10) === 0).length;
  const importRows    = rows.filter(r => !r._excluded && !(skipZeroQty && parseInt(r.quantity, 10) === 0));

  const BATCH_SIZE = 100;
  const handleImport = async () => {
    setImporting(true); setResult(null);
    const allApiRows = importRows.map(r => ({
      part_number:          r.part_number,
      name:                 r.name,
      quantity:             r.quantity,
      serial_number:        r.serial_number,
      status:               r.status,
      location_name:        r.location_name,
      purchase_price:       r.purchase_price,
      notes:                r.notes,
      inventree_stock_id:   r._inventree_stock_id,
    }));
    const acc = { imported: 0, updated: 0, skipped: 0, errors: [], new_locations: [] };
    setImportProgress({ done: 0, total: allApiRows.length });
    for (let i = 0; i < allApiRows.length; i += BATCH_SIZE) {
      try {
        const res = await api.post('import/inventory', { rows: allApiRows.slice(i, i + BATCH_SIZE), upsert });
        acc.imported += res.imported || 0;
        acc.updated  += res.updated  || 0;
        acc.skipped  += res.skipped  || 0;
        (res.errors        || []).forEach(e => acc.errors.push(e));
        (res.new_locations || []).forEach(l => acc.new_locations.push(l));
      } catch (e) { acc.errors.push({ row: i + 1, error: e?.data?.error || e.message }); }
      setImportProgress({ done: Math.min(i + BATCH_SIZE, allApiRows.length), total: allApiRows.length });
    }
    setResult(acc);
    setImportProgress(null);
    setImporting(false);
  };

  const handleDeleteLegacy = async () => {
    if (deleteLegacyConfirm !== 'DELETE') return;
    setDeletingLegacy(true);
    try {
      const res = await api.post('import/inventory/delete-legacy', {});
      setDeleteResult({ deleted: res.deleted });
    } catch (e) {
      setDeleteResult({ error: e?.data?.error || e.message });
    }
    setDeletingLegacy(false);
    setDeleteLegacyConfirm('');
  };

  const DangerZone = () => (
    <div className="mt-6 border border-red-200 rounded-lg overflow-hidden">
      <button
        className="w-full px-3 py-2 bg-red-50 border-b border-red-200 flex items-center justify-between"
        onClick={() => setShowDeleteZone(p => !p)}
      >
        <span className="text-[10px] font-bold text-red-700">Danger Zone — Delete All InvenTree-Imported Items</span>
        <span className="text-[8px] text-red-400">Admin only · irreversible {showDeleteZone ? '▲' : '▼'}</span>
      </button>
      {showDeleteZone && (
        <div className="p-3 flex flex-col gap-2">
          {deleteResult && (
            <div className={`text-[9px] rounded px-3 py-2 mb-1 ${deleteResult.error ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'}`}>
              {deleteResult.error ? `Error: ${deleteResult.error}` : `Deleted ${deleteResult.deleted} InvenTree-imported items.`}
            </div>
          )}
          <div className="text-[9px] text-gray-500 mb-1">
            Wipes all items imported from InvenTree so you can re-import a cleansed file. Does not affect items added manually or via PO.
          </div>
          <div className="text-[9px] text-gray-600 mb-2">Type <strong>DELETE</strong> to confirm:</div>
          <div className="flex gap-2 items-center">
            <input
              className="border border-gray-300 rounded px-3 py-1.5 text-[10px] font-mono focus:outline-none focus:border-red-400 flex-1 max-w-[200px]"
              placeholder="DELETE"
              value={deleteLegacyConfirm}
              onChange={e => setDeleteLegacyConfirm(e.target.value)}
            />
            <button
              onClick={handleDeleteLegacy}
              disabled={deleteLegacyConfirm !== 'DELETE' || deletingLegacy}
              className="px-4 py-1.5 text-[10px] font-bold bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-40"
            >
              {deletingLegacy ? 'Deleting…' : 'Delete All InvenTree Items'}
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const MappingTable = () => (
    <div className="mb-5 border border-gray-200 rounded-lg overflow-hidden">
      <div className="px-3 py-2 bg-navy text-white text-[10px] font-bold flex items-center justify-between">
        <span>Column Mapping — InvenTree Stock Export</span>
        <span className="text-white/40 font-normal text-[8px]">locked to this export format</span>
      </div>
      <div className="divide-y divide-gray-100">
        <div className="grid grid-cols-3 px-3 py-1.5 bg-gray-50 text-[8px] font-bold uppercase text-gray-400">
          <span>InvenTree Column</span><span>→ App Field</span><span>Notes</span>
        </div>
        {[
          { csv: 'Part Name',       field: 'part_number + name', note: 'split on " | " → code + description' },
          { csv: 'Quantity',        field: 'quantity',           note: 'rounded to integer' },
          { csv: 'Serial',          field: 'serial_number',      note: '' },
          { csv: 'Status',          field: 'status',             note: 'mapped — see below' },
          { csv: 'Location Name',   field: 'location_name',      note: 'auto-created if new' },
          { csv: 'purchase_price',  field: 'purchase_price',     note: '' },
          { csv: 'Notes',           field: 'notes',              note: '' },
          { csv: 'Stock Item ID',   field: '(reference)',        note: 'used for upsert matching' },
        ].map(col => (
          <div key={col.csv} className="grid grid-cols-3 px-3 py-1.5 text-[9px] hover:bg-gray-50/80">
            <div className="flex items-center gap-1.5">
              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${col.field.startsWith('(') ? 'bg-gray-300' : 'bg-emerald-500'}`} />
              <span className="font-medium text-gray-700">{col.csv}</span>
            </div>
            <div className="font-mono text-navy font-semibold">{col.field}</div>
            <div className="text-gray-400 text-[8px]">{col.note}</div>
          </div>
        ))}
        <div className="px-3 py-2 bg-gray-50/80">
          <div className="text-[8px] text-gray-400 font-medium mb-1">Skipped (not imported):</div>
          <div className="flex flex-wrap gap-1">
            {INVENTREE_SKIPPED.map(s => (
              <span key={s} className="text-[8px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{s}</span>
            ))}
          </div>
        </div>
        <div className="px-3 py-2 bg-amber-50/60 border-t border-amber-100">
          <div className="text-[8px] text-amber-700 font-semibold mb-1.5">Status mapping (applied on upload):</div>
          <div className="grid grid-cols-3 gap-x-6 gap-y-1">
            {Object.entries(INVENTREE_STATUS_MAP).map(([from, to]) => (
              <div key={from} className="flex items-center gap-1.5 text-[8px]">
                <span className="font-mono bg-amber-100 text-amber-800 px-1 py-0.5 rounded capitalize">{from}</span>
                <span className="text-gray-400">→</span>
                <span className="font-semibold text-gray-700">{to}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  if (rows.length === 0) {
    return (
      <div>
        {/* ── Full JSON import (preferred) ── */}
        <JsonImportSection />

        <div className="flex items-center gap-3 my-5">
          <div className="flex-1 border-t border-gray-200" />
          <span className="text-[9px] text-gray-400 uppercase tracking-wide font-semibold">or import from CSV</span>
          <div className="flex-1 border-t border-gray-200" />
        </div>

        <div className="mb-4">
          <div className="text-[11px] font-semibold text-navy mb-0.5">Import from InvenTree CSV</div>
          <div className="text-[9px] text-gray-500">
            Upload a stock export CSV for manual review and editing before import.
            Locations are auto-created if they don't exist.
          </div>
        </div>
        <MappingTable />
        <div
          className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-navy transition-colors mb-4"
          onDragOver={e => e.preventDefault()}
          onDrop={e => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
          onClick={() => inputRef.current?.click()}
        >
          <input ref={inputRef} type="file" accept=".csv,.xlsx,.xls" className="hidden"
            onChange={e => handleFile(e.target.files[0])} />
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-8 h-8 mx-auto mb-2 text-gray-400">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"/>
          </svg>
          <div className="text-[10px] text-gray-500">{fileName || 'Drop the InvenTree CSV export here, or click to browse'}</div>
        </div>
        {sheetNames.length > 1 && (
          <div className="flex items-center gap-2 flex-wrap mb-3">
            <span className="text-[10px] font-semibold text-gray-600">Sheet:</span>
            {sheetNames.map(n => (
              <button key={n} onClick={() => { setActiveSheet(n); loadSheet(fileBuffer, n); }}
                className={`px-2.5 py-1 text-[9px] font-semibold rounded border transition-colors ${activeSheet === n ? 'bg-navy text-white border-navy' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
                {n}
              </button>
            ))}
          </div>
        )}
        <DangerZone />
      </div>
    );
  }

  return (
    <div>
      {/* Summary bar */}
      <div className="flex items-center gap-4 mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200 flex-wrap">
        <div className="text-[10px]"><span className="text-xl font-extrabold text-navy">{rows.length}</span><span className="text-gray-500 ml-1">rows loaded</span></div>
        {issueCount > 0 && <div className="text-[10px]"><span className="text-xl font-extrabold text-amber-600">{issueCount}</span><span className="text-amber-600 ml-1">with issues</span></div>}
        {excludedCount > 0 && <div className="text-[10px]"><span className="text-xl font-extrabold text-gray-400">{excludedCount}</span><span className="text-gray-400 ml-1">excluded</span></div>}
        {importing && importProgress
          ? (
            <div className="flex-1 min-w-[140px]">
              <div className="flex justify-between text-[8px] text-gray-500 mb-0.5">
                <span>Importing…</span>
                <span>{importProgress.done} / {importProgress.total} rows</span>
              </div>
              <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div className="h-full bg-navy rounded-full transition-all duration-300" style={{ width: `${Math.max(2, (importProgress.done / importProgress.total) * 100)}%` }} />
              </div>
            </div>
          )
          : <div className="text-[10px] flex-1 text-right"><span className="text-xl font-extrabold text-emerald-600">{importRows.length}</span><span className="text-emerald-600 ml-1">will import</span></div>
        }
        <label className="flex items-center gap-1.5 text-[10px] text-gray-600 cursor-pointer select-none whitespace-nowrap">
          <input type="checkbox" checked={skipZeroQty} onChange={e => setSkipZeroQty(e.target.checked)} className="rounded border-gray-300" />
          Skip zero-qty ({zeroQtyCount})
        </label>
        <label className="flex items-center gap-1.5 text-[10px] text-gray-600 cursor-pointer select-none whitespace-nowrap">
          <input type="checkbox" checked={upsert} onChange={e => setUpsert(e.target.checked)} className="rounded border-gray-300" />
          Update existing (by InvenTree ID)
        </label>
        <button onClick={handleImport} disabled={importing || importRows.length === 0}
          className="px-4 py-2 bg-navy text-white text-[10px] font-bold rounded hover:bg-navy/80 disabled:opacity-50 whitespace-nowrap">
          {importing ? 'Importing…' : `Import ${importRows.length} rows →`}
        </button>
        <button onClick={() => { setRows([]); setFileName(''); setResult(null); }}
          className="px-3 py-2 border border-gray-200 text-[10px] rounded hover:bg-gray-50 text-gray-600">
          ← New file
        </button>
      </div>

      {/* Import result */}
      {result && (
        <div className={`mb-4 rounded p-4 border ${result.error ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'}`}>
          {result.error ? (
            <div className="text-[10px] text-red-700">{result.error}</div>
          ) : (
            <>
              <div className="flex gap-6 mb-2 flex-wrap">
                {[
                  { label: 'Imported', value: result.imported, cls: 'text-emerald-700' },
                  ...(result.updated  > 0 ? [{ label: 'Updated',  value: result.updated,  cls: 'text-blue-600'  }] : []),
                  ...(result.skipped  > 0 ? [{ label: 'Skipped',  value: result.skipped,  cls: 'text-gray-500'  }] : []),
                  ...(result.new_locations?.length > 0 ? [{ label: 'New Locations', value: result.new_locations.length, cls: 'text-purple-600' }] : []),
                ].map(s => (
                  <div key={s.label} className="text-center">
                    <div className={`text-2xl font-extrabold ${s.cls}`}>{s.value}</div>
                    <div className="text-[8px] uppercase font-bold text-gray-500">{s.label}</div>
                  </div>
                ))}
              </div>
              {result.new_locations?.length > 0 && (
                <div className="text-[9px] text-purple-700 mb-2">New locations created: {result.new_locations.join(', ')}</div>
              )}
              {result.errors?.length > 0 && (
                <div className="border border-red-200 bg-red-50 rounded p-2 mt-2">
                  <div className="text-[9px] font-bold text-red-700 mb-1">{result.errors.length} rows failed</div>
                  <div className="space-y-0.5 max-h-28 overflow-y-auto">
                    {result.errors.map((e, i) => <div key={i} className="text-[8px] text-red-600">Row {e.row}: {e.error}</div>)}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Sheet selector */}
      {sheetNames.length > 1 && (
        <div className="flex items-center gap-1.5 mb-3">
          <span className="text-[10px] font-semibold text-gray-500">Sheet:</span>
          {sheetNames.map(n => (
            <button key={n} onClick={() => { setActiveSheet(n); loadSheet(fileBuffer, n); }}
              className={`px-2.5 py-0.5 text-[9px] font-semibold rounded border transition-colors ${activeSheet === n ? 'bg-navy text-white border-navy' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
              {n}
            </button>
          ))}
        </div>
      )}

      {/* Filter bar */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, part #, location, status…"
          className="border border-gray-200 rounded px-2 py-1 text-[10px] focus:outline-none focus:border-navy w-52" />
        <label className="flex items-center gap-1.5 text-[10px] text-amber-700 cursor-pointer select-none">
          <input type="checkbox" checked={issuesOnly} onChange={e => setIssuesOnly(e.target.checked)} className="rounded border-gray-300" />
          Issues only ({issueCount})
        </label>
        {issueCount > 0 && (
          <button onClick={() => setRows(prev => prev.map(r => getIssues(r).length > 0 ? { ...r, _excluded: true } : r))}
            className="px-2.5 py-1 text-[9px] text-red-600 border border-red-200 rounded hover:bg-red-50">
            Exclude all with issues
          </button>
        )}
        <span className="text-[9px] text-gray-400 ml-auto">Click any cell to edit · uncheck to exclude from import</span>
      </div>

      {/* Cleanse grid */}
      <div className="border border-gray-200 rounded">
        <div className="overflow-x-auto overflow-y-auto max-h-[55vh]">
          <table className="min-w-max w-full text-[9px]">
            <thead className="sticky top-0 z-10 bg-gray-50">
              <tr className="border-b border-gray-200">
                <th className="px-2 py-2 w-8 shrink-0 text-center"><span className="text-[7px] text-gray-400 uppercase">Incl.</span></th>
                <th className="px-1 py-2 text-gray-400 text-left w-8">#</th>
                {INVENTREE_SCHEMA.map(col => (
                  <th key={col.field} className="px-2 py-2 text-left whitespace-nowrap border-l border-gray-100">
                    <div className="font-bold text-gray-600 text-[9px]">{col.label}</div>
                    <div className="text-[7px] text-gray-400 font-normal font-mono">{col.field}</div>
                  </th>
                ))}
                <th className="px-2 py-2 text-left whitespace-nowrap text-[8px] font-bold text-gray-400 uppercase border-l border-gray-100">Issues</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((row, vi) => {
                const issues = getIssues(row);
                const hasIssue = issues.length > 0;
                return (
                  <tr key={row._id}
                    className={`border-b border-gray-100 transition-colors ${row._excluded ? 'opacity-35 bg-gray-50 line-through' : hasIssue ? 'bg-amber-50/50' : 'hover:bg-blue-50/20'}`}>
                    <td className="px-2 py-1 text-center">
                      <input type="checkbox" checked={!row._excluded} onChange={() => toggleExclude(row._id)} className="rounded border-gray-300" />
                    </td>
                    <td className="px-1 py-1 text-gray-300 text-right">{vi + 1}</td>
                    {INVENTREE_SCHEMA.map(col => {
                      const val = row[col.field] ?? '';
                      const isEditing = editCell?.rowId === row._id && editCell?.field === col.field;
                      const isMissed = col.required && !String(val).trim();
                      const isStatusBad = col.type === 'status' && val && !VALID_INVENTORY_STATUSES.includes(val);
                      return (
                        <td key={col.field}
                          className="px-1 py-0.5 border-l border-gray-100"
                          onClick={() => !row._excluded && setEditCell({ rowId: row._id, field: col.field })}>
                          {isEditing ? (
                            col.type === 'status' ? (
                              <select autoFocus value={val}
                                onChange={e => updateCell(row._id, col.field, e.target.value)}
                                onBlur={() => setEditCell(null)}
                                className="border border-navy rounded px-1 py-0.5 text-[9px] bg-white focus:outline-none">
                                {VALID_INVENTORY_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                              </select>
                            ) : (
                              <input autoFocus value={val}
                                onChange={e => updateCell(row._id, col.field, e.target.value)}
                                onBlur={() => setEditCell(null)}
                                onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Escape') setEditCell(null); }}
                                className="border border-navy rounded px-1 py-0.5 text-[9px] w-full min-w-[80px] focus:outline-none bg-white" />
                            )
                          ) : (
                            <div
                              title={String(val)}
                              className={`px-1 py-0.5 rounded cursor-pointer hover:bg-white hover:ring-1 hover:ring-blue-300 truncate max-w-[140px] ${
                                isMissed       ? 'text-red-400 italic text-[8px]' :
                                isStatusBad    ? 'text-red-600 font-bold' :
                                col.field === 'status' ? (
                                  val === 'in_stock'      ? 'text-emerald-700 font-semibold' :
                                  val === 'decommissioned'? 'text-gray-400' :
                                  val === 'allocated'     ? 'text-amber-700 font-semibold' :
                                  val === 'deployed'      ? 'text-purple-700 font-semibold' :
                                  'text-blue-700 font-semibold'
                                ) :
                                col.field === 'part_number' ? 'font-mono font-semibold text-navy text-[8px]' :
                                col.field === 'name'        ? 'font-medium text-gray-800' :
                                col.type   === 'number'     ? 'font-mono text-right text-gray-700' :
                                col.type   === 'money'      ? 'text-emerald-700 font-semibold' :
                                'text-gray-700'
                              }`}>
                              {isMissed ? '⚠ required' :
                               col.type === 'money' ? (val ? `£${parseFloat(val).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : <span className="text-gray-300">—</span>) :
                               val || <span className="text-gray-300">—</span>}
                            </div>
                          )}
                        </td>
                      );
                    })}
                    <td className="px-2 py-1 border-l border-gray-100">
                      {issues.length > 0 && (
                        <div className="flex flex-col gap-0.5">
                          {issues.map((iss, i) => (
                            <span key={i} className="text-[7px] bg-amber-100 text-amber-800 px-1 py-0.5 rounded whitespace-nowrap">{iss}</span>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {visible.length === 0 && (
                <tr><td colSpan={INVENTREE_SCHEMA.length + 3} className="text-center py-8 text-gray-400 text-[10px]">No rows match your filter</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="px-3 py-1.5 border-t bg-gray-50 flex items-center justify-between text-[8px] text-gray-400">
          <span>Showing {visible.length} of {rows.length} rows</span>
          <span>Click a cell to edit in-place · status shows dropdown · Enter or Escape to confirm</span>
        </div>
      </div>
      <DangerZone />
    </div>
  );
}

// ── Bulk PO Number Update ─────────────────────────────────────────────────────
function BulkPoUpdate() {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult]   = useState(null);
  const fileRef = useRef(null);

  const parseFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const wb = XLSX.read(e.target.result, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const raw = XLSX.utils.sheet_to_json(ws, { defval: '' });
      const parsed = [];
      for (const row of raw) {
        const normRow = {};
        for (const [k, v] of Object.entries(row)) normRow[norm(k)] = String(v ?? '').trim();
        const ref = normRow['sow reference'] || normRow['reference'] || normRow['sow ref'] || normRow['av reference'] || normRow['av ref'] || '';
        const po  = normRow['po number'] || normRow['po_number'] || normRow['client po'] || normRow['purchase order number'] || normRow['purchase order'] || '';
        if (ref) parsed.push({ reference: ref.trim(), po_number: po.trim() });
      }
      setRows(parsed);
      setResult(null);
    };
    reader.readAsArrayBuffer(file);
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (file) parseFile(file);
  };

  const handleApply = async () => {
    if (!rows.length) return;
    setLoading(true);
    setResult(null);
    try {
      const data = await api.post('import/sows/po-bulk-update', { rows });
      setResult(data);
      if (data.rows) {
        setRows(data.rows);
      }
    } catch (e) {
      setResult({ error: e?.data?.error || 'Update failed' });
    } finally {
      setLoading(false);
    }
  };

  const clearAll = () => { setRows([]); setResult(null); if (fileRef.current) fileRef.current.value = ''; };

  return (
    <div>
      <h3 className="text-[11px] font-extrabold text-navy mb-1">Bulk Update Client PO Numbers</h3>
      <p className="text-[9px] text-gray-500 mb-4">
        Upload a CSV or Excel file with two columns: <strong>SOW Reference</strong> and <strong>PO Number</strong>.
        Each matching SOW will have its client PO number set to the value provided. Blank PO Number values will clear the field.
      </p>

      <div className="flex items-center gap-3 mb-4">
        <label className="cursor-pointer px-3 py-1.5 text-[9px] font-semibold border border-gray-300 rounded hover:border-blue-400 hover:bg-blue-50 transition-colors">
          Choose file (CSV / Excel)
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={handleFile} />
        </label>
        {rows.length > 0 && (
          <>
            <span className="text-[9px] text-gray-500">{rows.length} row{rows.length !== 1 ? 's' : ''} loaded</span>
            <button onClick={handleApply} disabled={loading}
              className="px-3 py-1.5 text-[9px] font-semibold bg-navy text-white rounded hover:bg-navy/80 disabled:opacity-50">
              {loading ? 'Applying…' : 'Apply Updates'}
            </button>
            <button onClick={clearAll} className="text-[9px] text-gray-400 hover:text-gray-600 underline">Clear</button>
          </>
        )}
      </div>

      {result && !result.error && (
        <div className="mb-3 p-2.5 rounded border text-[9px] bg-green-50 border-green-200 text-green-700">
          <strong>{result.updated}</strong> SOW{result.updated !== 1 ? 's' : ''} updated
          {result.not_found > 0 && <span className="ml-3 text-amber-600"><strong>{result.not_found}</strong> reference{result.not_found !== 1 ? 's' : ''} not found</span>}
        </div>
      )}
      {result?.error && (
        <div className="mb-3 p-2.5 rounded border text-[9px] bg-red-50 border-red-200 text-red-700">{result.error}</div>
      )}

      {rows.length > 0 && (
        <div className="border border-gray-200 rounded overflow-x-auto">
          <table className="w-full text-[9px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-3 py-2 font-semibold text-gray-600">SOW Reference</th>
                <th className="text-left px-3 py-2 font-semibold text-gray-600">PO Number</th>
                {result && <th className="text-left px-3 py-2 font-semibold text-gray-600">Status</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className={`border-b border-gray-50 ${r.status === 'not_found' ? 'bg-red-50' : r.status === 'updated' ? 'bg-green-50' : ''}`}>
                  <td className="px-3 py-1.5 font-mono">{r.reference}</td>
                  <td className="px-3 py-1.5 font-mono">{r.po_number || <span className="text-gray-300 italic">blank</span>}</td>
                  {result && (
                    <td className="px-3 py-1.5">
                      {r.status === 'updated'   && <span className="text-green-600 font-semibold">Updated</span>}
                      {r.status === 'not_found' && <span className="text-red-500 font-semibold">Not found</span>}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!rows.length && (
        <div className="border-2 border-dashed border-gray-200 rounded p-8 text-center">
          <div className="text-[9px] text-gray-400 mb-2">Upload a file to preview before applying</div>
          <div className="text-[8px] text-gray-300">Accepted columns: SOW Reference / Reference / AV Reference, PO Number / Client PO / Purchase Order Number</div>
        </div>
      )}
    </div>
  );
}

// ── Sync Forecast Completion from Milestones ─────────────────────────────────
// Exported: reused on ZohoCutoverImportPage as a repair tool for any
// sows.forecast_completion values corrupted by the (now-fixed) date-parsing
// bug in the Zoho cutover import, since this is the single source of truth
// for recomputing that derived field correctly.
export function SyncForecastCompletion() {
  const [preview, setPreview]   = useState(null);  // { total, changed, rows }
  const [loading, setLoading]   = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult]     = useState(null);

  const loadPreview = async () => {
    setLoading(true);
    setResult(null);
    try {
      const data = await api.get('import/sows/sync-forecast-completion');
      setPreview(data);
    } catch (e) {
      setPreview({ error: e?.data?.error || 'Failed to load preview' });
    } finally {
      setLoading(false);
    }
  };

  const applySync = async () => {
    setApplying(true);
    try {
      const data = await api.post('import/sows/sync-forecast-completion', {});
      setResult(data);
      setPreview(null);
    } catch (e) {
      setResult({ error: e?.data?.error || 'Update failed' });
    } finally {
      setApplying(false);
    }
  };

  const fmtDate = (s) => {
    if (!s) return <span className="text-gray-300 italic">—</span>;
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : s;
  };

  return (
    <div>
      <h3 className="text-[11px] font-extrabold text-navy mb-1">Sync Expected Completion from Milestones</h3>
      <p className="text-[9px] text-gray-500 mb-4">
        Sets each opportunity's <strong>Expected Completion Date</strong> to the latest forecast date across all its active milestones.
        Cancelled and Split milestones are excluded. SOWs with no active milestone forecast dates are left unchanged.
      </p>

      {!preview && !result && (
        <button onClick={loadPreview} disabled={loading}
          className="px-3 py-1.5 text-[9px] font-semibold border border-gray-300 rounded hover:border-blue-400 hover:bg-blue-50 transition-colors disabled:opacity-50">
          {loading ? 'Loading preview…' : 'Preview changes'}
        </button>
      )}

      {preview?.error && (
        <div className="p-2.5 rounded border text-[9px] bg-red-50 border-red-200 text-red-700">{preview.error}</div>
      )}

      {result && !result.error && (
        <div className="p-2.5 rounded border text-[9px] bg-green-50 border-green-200 text-green-700 mb-3">
          <strong>{result.updated}</strong> opportunit{result.updated !== 1 ? 'ies' : 'y'} updated.
          <button onClick={() => { setResult(null); setPreview(null); }} className="ml-3 underline text-green-600 hover:text-green-800">Run again</button>
        </div>
      )}
      {result?.error && (
        <div className="p-2.5 rounded border text-[9px] bg-red-50 border-red-200 text-red-700">{result.error}</div>
      )}

      {preview && !preview.error && (
        <>
          <div className="mb-3 p-2.5 rounded border text-[9px] bg-blue-50 border-blue-200 text-blue-700 flex items-center justify-between flex-wrap gap-2">
            <span>
              <strong>{preview.total}</strong> opportunit{preview.total !== 1 ? 'ies' : 'y'} have milestone forecast dates ·
              <strong className="ml-1">{preview.changed}</strong> would be updated
              {preview.changed === 0 && <span className="ml-1 text-blue-500">(all already up to date)</span>}
            </span>
            <div className="flex gap-2">
              <button onClick={() => setPreview(null)} className="text-[9px] text-gray-400 hover:text-gray-600 underline">Cancel</button>
              {preview.changed > 0 && (
                <button onClick={applySync} disabled={applying}
                  className="px-3 py-1 text-[9px] font-semibold bg-navy text-white rounded hover:bg-navy/80 disabled:opacity-50">
                  {applying ? 'Applying…' : `Apply (${preview.changed} changes)`}
                </button>
              )}
            </div>
          </div>

          {preview.rows.length > 0 && (
            <div className="border border-gray-200 rounded overflow-x-auto">
              <table className="w-full text-[9px]">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold text-gray-600">SOW Reference</th>
                    <th className="text-left px-3 py-2 font-semibold text-gray-600">Name</th>
                    <th className="text-left px-3 py-2 font-semibold text-gray-600">Current Expected Completion</th>
                    <th className="text-left px-3 py-2 font-semibold text-gray-600">New Value (max forecast date)</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r, i) => (
                    <tr key={i} className="border-b border-gray-50">
                      <td className="px-3 py-1.5 font-mono">{r.reference}</td>
                      <td className="px-3 py-1.5 text-gray-600 max-w-[200px] truncate">{r.detail || '—'}</td>
                      <td className="px-3 py-1.5 text-gray-400">{fmtDate(r.current_date)}</td>
                      <td className="px-3 py-1.5 font-semibold text-emerald-700">{fmtDate(r.new_date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── SOW Tools tab — combines PO bulk update + forecast completion sync ────────
function SowToolsTab() {
  return (
    <div className="space-y-8">
      <BulkPoUpdate />
      <hr className="border-gray-200" />
      <SyncForecastCompletion />
    </div>
  );
}

// ── Zoho Reconcile tab (read-only comparison, makes no changes) ───────────────
const ZOHO_PROJECT_MAP = {
  'av reference': 'reference',
  'project name': 'name',
  'status':       'status',
  'owner':        'owner',
  'project group':'group',
  'client':       'client',
  'revenue budget':'value',
  'start date':   'start_date',
  'end date':     'end_date',
  'deadline':     'deadline',
};
// New milestone export format (2026). Billing status comes from "Status",
// delivery state from "Zoho Status"; the rest carry health/commentary.
const ZOHO_MS_MAP = {
  'sow':                 'reference',
  'milestone':           'code',
  'description':         'name',
  'pm':                  'owner',
  'contract date':       'contract_date',
  'forecast date':       'forecast_date',
  'amount':              'value',
  'po number':           'po',
  'status':              'status',        // billing status
  'zoho status':         'zoho_status',   // delivery state
  'delivery update':     'delivery_update',
  'dependency comments': 'dependency_comment',
  'dependency delay':    'dependency_delay',
  'confidence level':    'confidence',
  'risk reason':         'risk_reason',
  // Assumption, Project Name, Budgeted Hours are informational — not imported.
};

const FIELD_LABEL = {
  status: 'Status', name: 'Name', owner: 'Owner', client: 'Client', group: 'Group',
  value: 'Value (£)', po: 'PO Number', forecast: 'Forecast Date', end_date: 'End Date',
};

// value that would be written to ops if this field is applied
const applyValue = (f) => f.field === 'status' ? (f.zoho_mapped || f.zoho) : f.zoho;
const pickKey = (r, field) => `${r.ref}||${r.code || ''}||${field}`;

function ZohoReconcileTab() {
  const [type,     setType]     = useState('projects');
  const [fileName, setFileName] = useState('');
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);
  const [data,     setData]     = useState(null); // { summary, rows, only_ops }
  const [mappedRows, setMappedRows] = useState(null); // parsed Zoho rows, kept for re-compare after apply
  const [filter,   setFilter]   = useState('diff');
  const [search,   setSearch]   = useState('');
  const [expanded, setExpanded] = useState({});
  const [picks,    setPicks]    = useState({});       // pickKey → true (fields chosen to apply)
  const [confirmApply, setConfirmApply] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const inputRef = useRef();

  const colMap = type === 'projects' ? ZOHO_PROJECT_MAP : ZOHO_MS_MAP;

  const reset = () => {
    setData(null); setMappedRows(null); setError(null); setFileName('');
    setExpanded({}); setSearch(''); setFilter('diff'); setPicks({}); setApplyResult(null);
  };

  const runReconcile = async (rows, t) => {
    const res = await api.post('import/zoho/reconcile', { type: t, rows });
    setData(res);
  };

  const handleFile = async (file) => {
    if (!file) return;
    setError(null); setData(null); setFileName(file.name); setLoading(true); setPicks({}); setApplyResult(null);
    try {
      const buffer = await new Promise((res, rej) => {
        const fr = new FileReader();
        fr.onload = e => res(e.target.result);
        fr.onerror = () => rej(new Error('Read error'));
        fr.readAsArrayBuffer(file);
      });
      // Auto-detect the sheet whose headers best match this type's columns
      const prefer = Object.keys(colMap);
      const { rows: raw } = parseBuffer(buffer, '', prefer);
      const mapped = raw
        .filter(r => Object.values(r).some(v => String(v).trim() !== ''))
        .map(r => {
          const out = {};
          for (const [k, v] of Object.entries(r)) {
            const f = colMap[norm(k)];
            if (f && out[f] == null) out[f] = v; // first match wins (handles duplicate SOW column)
          }
          return out;
        });
      if (!mapped.length) { setError('No rows found — is this the right file / sheet?'); setLoading(false); return; }
      setMappedRows(mapped);
      await runReconcile(mapped, type);
      setFilter('diff');
    } catch (e) {
      setError(e?.data?.error || e.message || 'Reconcile failed');
    }
    setLoading(false);
  };

  // ── Apply selection ───────────────────────────────────────────────────────
  const togglePick = (r, field) =>
    setPicks(p => { const k = pickKey(r, field); const n = { ...p }; if (n[k]) delete n[k]; else n[k] = true; return n; });

  const selectAllDiffs = (includeBlanks) =>
    setPicks(() => {
      const next = {};
      for (const r of (data?.rows || [])) {
        if (r.state !== 'diff') continue;
        for (const f of r.fields) if (!f.match && (includeBlanks || !f.one_sided)) next[pickKey(r, f.field)] = true;
      }
      return next;
    });

  // Build the apply payload from current picks
  const changes = [];
  if (data) for (const r of data.rows) {
    if (r.state !== 'diff') continue;
    const updates = [];
    for (const f of r.fields) {
      if (!f.match && picks[pickKey(r, f.field)]) updates.push({ field: f.field, value: applyValue(f) });
    }
    if (updates.length) changes.push({ ref: r.ref, code: r.code, updates });
  }
  const selectedCount = changes.reduce((n, c) => n + c.updates.length, 0);

  // Accepted-difference (ignore) payload from the same picks — keyed on the raw Zoho value
  const ignoreItems = [];
  if (data) for (const r of data.rows) {
    if (r.state !== 'diff') continue;
    for (const f of r.fields) {
      if (!f.match && !f.ignored && picks[pickKey(r, f.field)]) {
        ignoreItems.push({ ref: r.ref, code: r.code, field: f.field, zoho_value: f.zoho, ops_value: f.ops });
      }
    }
  }

  const handleApply = async () => {
    setApplying(true); setApplyResult(null);
    try {
      const res = await api.post('import/zoho/apply', { type: data.summary.type, changes });
      setApplyResult(res);
      setPicks({});
      if (mappedRows) await runReconcile(mappedRows, data.summary.type); // refresh against new live state
    } catch (e) {
      setApplyResult({ error: e?.data?.error || e.message });
    }
    setApplying(false); setConfirmApply(false);
  };

  // Mark selected differences as OK — remembered for future uploads
  const handleIgnore = async () => {
    setApplying(true); setApplyResult(null);
    try {
      const res = await api.post('import/zoho/ignore', { type: data.summary.type, items: ignoreItems });
      setApplyResult({ ignored: res.added });
      setPicks({});
      if (mappedRows) await runReconcile(mappedRows, data.summary.type);
    } catch (e) {
      setApplyResult({ error: e?.data?.error || e.message });
    }
    setApplying(false);
  };

  const handleUnignore = async (r, f) => {
    try {
      await api.post('import/zoho/unignore', { type: data.summary.type, items: [{ ref: r.ref, code: r.code, field: f.field, zoho_value: f.zoho }] });
      if (mappedRows) await runReconcile(mappedRows, data.summary.type);
    } catch (e) { /* surfaced on next reconcile */ }
  };

  const summary = data?.summary;
  const CARDS = summary ? [
    { id: 'all',       label: 'Zoho rows',     value: summary.zoho_rows,  cls: 'text-navy' },
    { id: 'match',     label: 'In sync',       value: summary.matched,    cls: 'text-emerald-600' },
    { id: 'diff',      label: 'Differing',     value: summary.differing,  cls: 'text-amber-600' },
    { id: 'only_zoho', label: 'Only in Zoho',  value: summary.only_zoho,  cls: 'text-blue-600' },
    { id: 'only_ops',  label: 'Only in ops',   value: summary.only_ops,   cls: 'text-purple-600' },
    { id: 'accepted',  label: 'Accepted',      value: summary.accepted ?? 0, cls: 'text-gray-500' },
    { id: 'skipped',   label: 'Done (skipped)', value: summary.skipped ?? 0, cls: 'text-gray-400' },
    { id: 'invalid',   label: 'Unmatchable',   value: summary.invalid,    cls: 'text-red-500' },
  ] : [];

  // Rows shown depend on the active filter card
  let shown = [];
  if (data) {
    if (filter === 'only_ops') {
      shown = data.only_ops.map(o => ({ ...o, state: 'only_ops', fields: [] }));
    } else if (filter === 'all') {
      shown = data.rows;
    } else if (filter === 'accepted') {
      shown = data.rows.filter(r => (r.ignored_count || 0) > 0);
    } else {
      shown = data.rows.filter(r => r.state === filter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      shown = shown.filter(r =>
        String(r.ref || '').toLowerCase().includes(q) ||
        String(r.code || '').toLowerCase().includes(q) ||
        String(r.name || '').toLowerCase().includes(q));
    }
  }

  const stateBadge = {
    diff:      { t: 'Differs',      c: 'bg-amber-100 text-amber-800' },
    match:     { t: 'In sync',      c: 'bg-emerald-100 text-emerald-700' },
    only_zoho: { t: 'Only in Zoho', c: 'bg-blue-100 text-blue-700' },
    only_ops:  { t: 'Only in ops',  c: 'bg-purple-100 text-purple-700' },
    skipped:   { t: 'Done — skipped', c: 'bg-gray-100 text-gray-500' },
    invalid:   { t: 'Unmatchable',  c: 'bg-red-100 text-red-600' },
  };

  return (
    <div>
      <div className="mb-4">
        <div className="text-[11px] font-semibold text-navy mb-0.5">Zoho Reconcile</div>
        <div className="text-[9px] text-gray-500">
          Upload a Zoho export and compare it line-by-line against the live ops data. Comparing is
          read-only; you then <strong className="text-gray-700">tick the specific values you want and apply them</strong> to
          the ops tool — or <strong className="text-gray-700">mark a difference as OK</strong>, which is remembered so it
          won't be flagged on future uploads. Status values are mapped (e.g. Zoho <em>On Track</em> → ops <em>Active</em>)
          and known-equivalent clients (e.g. <em>Rolls-Royce Submarines</em> = <em>Rolls-Royce Submarines Ltd</em>) are treated as matching.
        </div>
      </div>

      <div className="flex gap-2 mb-4">
        {[['projects','Projects → SOWs'],['milestones','Milestones']].map(([id, label]) => (
          <button key={id} onClick={() => { setType(id); reset(); }}
            className={`px-4 py-2 text-[10px] font-semibold rounded border transition-colors ${type === id ? 'bg-navy text-white border-navy' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
            {label}
          </button>
        ))}
      </div>

      {!data && (
        <div
          className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-navy transition-colors mb-4"
          onDragOver={e => e.preventDefault()}
          onDrop={e => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
          onClick={() => inputRef.current?.click()}
        >
          <input ref={inputRef} type="file" accept=".csv,.xlsx,.xls" className="hidden"
            onChange={e => handleFile(e.target.files[0])} />
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-8 h-8 mx-auto mb-2 text-gray-400">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"/>
          </svg>
          <div className="text-[10px] text-gray-500">
            {loading ? 'Comparing against live data…' : (fileName || `Drop the Zoho ${type === 'projects' ? 'projects' : 'milestones'} export here, or click to browse`)}
          </div>
        </div>
      )}

      {error && <div className="mb-4 text-[10px] text-red-700 bg-red-50 border border-red-200 rounded p-3">{error}</div>}

      {data && (
        <>
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <button onClick={reset} className="px-3 py-1.5 border border-gray-200 text-[10px] rounded hover:bg-gray-50 text-gray-600">← New file</button>
            <span className="text-[9px] text-gray-400">{fileName} · compared against {summary.ops_total} ops records</span>
          </div>

          {/* Summary cards double as filters */}
          <div className="grid grid-cols-3 sm:grid-cols-7 gap-2 mb-4">
            {CARDS.map(c => (
              <button key={c.id} onClick={() => setFilter(c.id)}
                className={`text-center p-2 rounded border transition-colors ${filter === c.id ? 'border-navy bg-navy/5' : 'border-gray-200 hover:bg-gray-50'}`}>
                <div className={`text-xl font-extrabold ${c.cls}`}>{c.value}</div>
                <div className="text-[8px] uppercase font-bold text-gray-500">{c.label}</div>
              </button>
            ))}
          </div>

          {/* Apply result banner */}
          {applyResult && (
            <div className={`mb-3 rounded p-3 border text-[10px] ${applyResult.error ? 'bg-red-50 border-red-200 text-red-700' : 'bg-emerald-50 border-emerald-200 text-emerald-800'}`}>
              {applyResult.error ? `Apply failed: ${applyResult.error}` : applyResult.ignored != null ? (
                <span>Accepted <strong>{applyResult.ignored}</strong> difference{applyResult.ignored === 1 ? '' : 's'} — these won’t be flagged on future uploads (until the Zoho value changes). Re-compared below.</span>
              ) : (
                <span>
                  Applied <strong>{applyResult.field_count}</strong> value{applyResult.field_count === 1 ? '' : 's'} across <strong>{applyResult.updated}</strong> record{applyResult.updated === 1 ? '' : 's'}.
                  {applyResult.created_clients > 0 && <> Created {applyResult.created_clients} new client{applyResult.created_clients === 1 ? '' : 's'}.</>}
                  {applyResult.errors?.length > 0 && <span className="text-amber-700"> {applyResult.errors.length} could not be applied (e.g. unmatched owner names).</span>}
                  {' '}The table below has been re-compared against the updated ops data.
                </span>
              )}
            </div>
          )}

          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search ref / name…"
              className="border border-gray-200 rounded px-2 py-1 text-[10px] focus:outline-none focus:border-navy w-48" />
            {filter === 'diff' && summary.differing > 0 && (
              <>
                <button onClick={() => selectAllDiffs(false)} className="px-2 py-1 text-[9px] border border-gray-200 rounded hover:bg-gray-50 text-gray-600">Select all differences</button>
                {selectedCount > 0 && <button onClick={() => setPicks({})} className="px-2 py-1 text-[9px] border border-gray-200 rounded hover:bg-gray-50 text-gray-500">Clear ({selectedCount})</button>}
              </>
            )}
            <span className="text-[9px] text-gray-400 ml-auto">Showing {shown.length} {stateBadge[filter]?.t?.toLowerCase() || filter} rows</span>
          </div>

          {/* Apply bar */}
          {selectedCount > 0 && (
            <div className="flex items-center gap-3 mb-3 p-2.5 bg-navy/5 border border-navy/20 rounded flex-wrap">
              <span className="text-[10px] text-navy font-semibold">{selectedCount} value{selectedCount === 1 ? '' : 's'} selected across {changes.length} record{changes.length === 1 ? '' : 's'}</span>
              <div className="ml-auto flex items-center gap-2">
                <button onClick={handleIgnore} disabled={applying}
                  className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-[10px] font-bold rounded hover:bg-gray-50 disabled:opacity-50"
                  title="Mark these as acceptable — they won't be flagged on future uploads">
                  ✓ Mark as OK (ignore)
                </button>
                <button onClick={() => setConfirmApply(true)} disabled={applying}
                  className="px-4 py-1.5 bg-navy text-white text-[10px] font-bold rounded hover:bg-navy/80 disabled:opacity-50">
                  Apply to ops →
                </button>
              </div>
            </div>
          )}

          <div className="border border-gray-200 rounded overflow-hidden">
            <div className="overflow-x-auto max-h-[55vh]">
              <table className="min-w-full text-[9px]">
                <thead className="sticky top-0 bg-gray-50 z-10">
                  <tr className="border-b border-gray-200 text-left text-[8px] uppercase font-bold text-gray-400">
                    <th className="px-2 py-2">Reference</th>
                    {type === 'milestones' && <th className="px-2 py-2">Milestone</th>}
                    <th className="px-2 py-2">Name</th>
                    <th className="px-2 py-2">State</th>
                    <th className="px-2 py-2">Discrepancies / note</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((r, i) => {
                    const key = `${r.ref}|${r.code || ''}|${i}`;
                    const isOpen = expanded[key];
                    const diffFields = (r.fields || []).filter(f => !f.match && !f.ignored);
                    const badge = stateBadge[r.state] || stateBadge.diff;
                    return (
                      <FragmentRow key={key}
                        r={r} type={type} badge={badge} diffFields={diffFields} isOpen={isOpen}
                        picks={picks} onPick={togglePick} onUnignore={handleUnignore}
                        onToggle={() => setExpanded(p => ({ ...p, [key]: !p[key] }))} />
                    );
                  })}
                  {shown.length === 0 && (
                    <tr><td colSpan={type === 'milestones' ? 5 : 4} className="text-center py-8 text-gray-400 text-[10px]">No rows in this category 🎉</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-3 text-[8px] text-gray-400 space-y-0.5">
            <div><strong>In sync</strong> = every compared field agrees (after status mapping + ignore rules). <strong>Differing</strong> = at least one field disagrees — expand to tick which Zoho values to apply.</div>
            <div><strong>Only in Zoho</strong> = record exists in the export but not in ops (use the Legacy Tracker tab to create new records). <strong>Only in ops</strong> = exists in ops but absent from this export. <strong>Unmatchable</strong> = malformed/blank reference (e.g. column drift) so it can't be matched.</div>
            <div>Applying writes only the values you tick; every change is logged to the record's commentary and the audit trail. Nothing else is touched.</div>
          </div>
        </>
      )}

      {/* Confirm apply modal */}
      {confirmApply && (
        <Modal
          title={`Apply ${selectedCount} value${selectedCount === 1 ? '' : 's'} to the ops tool?`}
          onClose={() => { if (!applying) setConfirmApply(false); }}
        >
            <div className="text-[10px] text-gray-600 mb-4">
              This will overwrite the selected fields on {changes.length} ops {data?.summary?.type === 'projects' ? 'SOW' : 'milestone'} record{changes.length === 1 ? '' : 's'} with the Zoho values.
              This <strong>changes live data</strong> — it can't be auto-undone. Each change is recorded in the audit trail.
            </div>
            {applying && (
              <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden mb-3">
                <div className="h-full w-2/5 bg-navy/70 rounded-full" style={{ animation: 'progress-sweep 1.4s ease-in-out infinite' }} />
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirmApply(false)} disabled={applying}
                className="px-4 py-2 text-[10px] border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-40">Cancel</button>
              <button onClick={handleApply} disabled={applying}
                className="px-4 py-2 text-[10px] font-bold bg-navy text-white rounded hover:bg-navy/80 disabled:opacity-40">
                {applying ? 'Applying…' : `Apply ${selectedCount}`}
              </button>
            </div>
        </Modal>
      )}
    </div>
  );
}

// Single reconcile row + expandable field-level diff
function FragmentRow({ r, type, badge, diffFields, isOpen, onToggle, picks = {}, onPick, onUnignore }) {
  const acceptedFields = (r.fields || []).filter(f => f.ignored);
  const expandable = (r.fields || []).some(f => !f.match); // any diff or accepted-diff to show
  const selectable = r.state === 'diff' && typeof onPick === 'function';
  return (
    <>
      <tr className={`border-b border-gray-100 ${expandable ? 'cursor-pointer hover:bg-blue-50/30' : ''}`} onClick={expandable ? onToggle : undefined}>
        <td className="px-2 py-1.5 font-mono font-semibold text-navy whitespace-nowrap">{r.ref}</td>
        {type === 'milestones' && <td className="px-2 py-1.5 font-mono text-gray-600 whitespace-nowrap">{r.code}</td>}
        <td className="px-2 py-1.5 text-gray-700 max-w-[260px] truncate" title={r.name}>{r.name || <span className="text-gray-300">—</span>}</td>
        <td className="px-2 py-1.5"><span className={`px-1.5 py-0.5 rounded text-[8px] font-semibold ${badge.c}`}>{badge.t}</span></td>
        <td className="px-2 py-1.5 text-gray-500">
          {r.note ? <span className="text-[8px] text-gray-500 italic">{r.note}</span>
            : (diffFields.length > 0 || acceptedFields.length > 0)
              ? <span className="flex flex-wrap gap-1 items-center">
                  {diffFields.map(f => (
                    <span key={f.field} className={`text-[8px] px-1 py-0.5 rounded ${f.one_sided ? 'bg-gray-100 text-gray-500' : 'bg-amber-100 text-amber-800'}`}>{FIELD_LABEL[f.field] || f.field}</span>
                  ))}
                  {acceptedFields.length > 0 && <span className="text-[8px] px-1 py-0.5 rounded bg-gray-100 text-gray-400">✓ {acceptedFields.length} accepted</span>}
                  {expandable && <span className="text-[8px] text-gray-400">{isOpen ? '▾' : '▸'}</span>}
                </span>
              : <span className="text-[8px] text-emerald-600">match</span>}
        </td>
      </tr>
      {isOpen && expandable && (
        <tr className="bg-gray-50/60">
          <td colSpan={type === 'milestones' ? 5 : 4} className="px-3 py-2">
            <table className="w-full text-[8px]">
              <thead>
                <tr className="text-gray-400 uppercase font-bold text-left">
                  {selectable && <th className="py-1 w-12">Apply</th>}
                  <th className="py-1 w-24">Field</th>
                  <th className="py-1">Zoho</th>
                  <th className="py-1">Ops tool</th>
                  <th className="py-1 w-16" />
                </tr>
              </thead>
              <tbody>
                {r.fields.map(f => {
                  const canApply = selectable && !f.match && !f.ignored;
                  const checked = canApply && !!picks[`${r.ref}||${r.code || ''}||${f.field}`];
                  return (
                  <tr key={f.field} className={f.match && !f.ignored ? 'text-gray-400' : ''}>
                    {selectable && (
                      <td className="py-0.5">
                        {canApply
                          ? <input type="checkbox" checked={checked} onChange={() => onPick(r, f.field)} className="rounded border-gray-300"
                              title={f.one_sided ? 'Zoho/ops differ — one side is blank; applying will overwrite ops' : 'Apply the Zoho value to ops'} />
                          : <span className="text-gray-200">—</span>}
                      </td>
                    )}
                    <td className="py-0.5 font-semibold text-gray-600">{FIELD_LABEL[f.field] || f.field}</td>
                    <td className={`py-0.5 ${!f.match && !f.ignored ? 'text-amber-700 font-semibold' : ''}`}>
                      {f.zoho || <span className="text-gray-300">(blank)</span>}
                      {f.zoho_mapped && f.zoho_mapped.toLowerCase() !== (f.zoho || '').toLowerCase() && (
                        <span className="text-blue-600 ml-1">→ {f.zoho_mapped}</span>
                      )}
                    </td>
                    <td className={`py-0.5 ${!f.match && !f.ignored ? 'text-navy font-semibold' : ''}`}>{f.ops || <span className="text-gray-300">(blank)</span>}</td>
                    <td className="py-0.5 text-right">
                      {f.ignored
                        ? <span className="inline-flex items-center gap-1">
                            <span className="text-[7px] font-semibold text-gray-400 bg-gray-100 px-1 py-0.5 rounded">accepted</span>
                            {onUnignore && <button onClick={(e) => { e.stopPropagation(); onUnignore(r, f); }} className="text-[7px] text-gold hover:underline">undo</button>}
                          </span>
                        : null}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function ImportPage() {
  const [tab, setTab] = useState('legacy');

  return (
    <div className="fade-in">
      <div className="mb-4">
        <h2 className="text-base font-extrabold text-navy">Data Import</h2>
        <div className="text-[9px] text-gray-500">
          Import and cleanse data from legacy spreadsheets and external systems.
        </div>
      </div>

      <div className="flex gap-1 mb-5 border-b border-gray-200 flex-wrap">
        {[
          { id: 'inventory',  label: 'Import Inventory' },
          { id: 'legacy',     label: 'Legacy Tracker' },
          { id: 'zoho',       label: 'Zoho Reconcile' },
          { id: 'cleanse',    label: 'Data Cleanse' },
          { id: 'sows',       label: 'Import SOWs' },
          { id: 'milestones', label: 'Import Milestones' },
          { id: 'pos',        label: 'Import Purchase Orders' },
          { id: 'catalog',    label: 'Import Product Catalog' },
          { id: 'sow-tools',  label: 'SOW Data Tools' },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-[10px] font-semibold border-b-2 -mb-px transition-colors ${
              tab === t.id ? 'border-navy text-navy' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="bg-white border border-gray-200 rounded p-5">
        {tab === 'inventory' && <InventoryImportTab />}
        {tab === 'legacy' && <LegacyTrackerImport />}
        {tab === 'zoho' && <ZohoReconcileTab />}
        {tab === 'cleanse' && <CleanseTab />}
        {tab === 'sows' && (
          <ImportTab
            title="Import SOWs / Opportunities"
            description='Upload the "Opportunities" sheet (CSV or Excel). Rows whose Reference already exists are skipped. Client names are matched or auto-created. Owner names are matched against active people.'
            endpoint="sows"
            colMap={SOW_COL_MAP}
          />
        )}
        {tab === 'milestones' && (
          <ImportTab
            title="Import Milestones"
            description='Upload the "Milestones" sheet (CSV or Excel). The "Reference" column must be the SOW reference (e.g. AV-401234). Milestones already present for that SOW + code are skipped.'
            endpoint="milestones"
            colMap={MILESTONE_COL_MAP}
          />
        )}
        {tab === 'pos' && (
          <ImportTab
            title="Import Purchase Orders"
            description='Upload the ATV PO Tracker spreadsheet (CSV or Excel). Columns: PO Number, Subject/Description, Supplier, Value/Amount, Date Issued, Status, AV Reference, Raised By, Notes. Missing suppliers are auto-created. Enable "Update existing" to re-import and fix values for POs already in the system.'
            endpoint="pos"
            colMap={PO_COL_MAP}
            allowUpdate={true}
          />
        )}
        {tab === 'catalog' && (
          <ImportTab
            title="Import / Update Product Catalog"
            description='Upload a CSV or Excel file. Columns: Code, Description, Category, Manufacturer, Sell Price, Commission %, Notes, Active. Rows with a matching Code are updated; rows without a Code are created. Export the current catalog first to use as a template.'
            endpoint="product-catalog"
            colMap={CATALOG_COL_MAP}
          />
        )}
        {tab === 'sow-tools' && <SowToolsTab />}
      </div>

      <div className="mt-4 max-w-5xl text-[8px] text-gray-400 space-y-0.5">
        <div>Dates are accepted in DD/MM/YYYY, YYYY-MM-DD, or Excel serial number format.</div>
        <div>Monetary values (£, commas, spaces) are stripped automatically.</div>
        <div>Import is additive — existing records are never overwritten.</div>
      </div>
    </div>
  );
}
